'use client'

// ---------------------------------------------------------------------------
// Library store — the optimistic client for the Library backend.
//
// Shapes follow spec/48-library-data-architecture.md §3 (the wire contract,
// canonical in @/types/shared and re-exported here): typed properties with
// option rows, attrs store option IDS (labels resolve from the vocabulary, so
// renames are one-row operations), collections carry heroAssetId/tags/description/
// rev, everything soft-deletes (undo restores the SAME ids), and every
// mutation is a command dispatched through one seam that records
// {command, inverse} for CMD+Z (spec §5.4).
//
// Persistence: Postgres, via libraryTransport — dispatch applies the patch
// locally (synchronous, optimistic) and enqueues the command for ordered
// shipping to POST /library/:ws/commands; setWorkspace hydrates from
// GET /library/:ws. Uploads go straight to R2 (presigned PUT); a row keeps its
// `idb:<assetId>` src only until its upload lands, then swaps to https.
// IndexedDB remains ONLY as the local media-blob cache (instant previews,
// undo of replace-media) — the old workspace snapshot layer is gone: the
// server is the source of truth.
// ---------------------------------------------------------------------------

import { create } from 'zustand'

import { SUPPORTED_VIDEO_FORMATS } from '@/types/shared'
import type {
  AttrValue,
  LibAsset,
  LibAssetVersion,
  LibCollection,
  LibraryAssetKind,
  LibraryCommand,
  LibraryProperty,
  LibraryPropertyConfig,
  LibraryPropertyOption,
  LibraryPropertyType,
  LibrarySnapshot,
  LibrarySourceKind,
  LibraryViewDefinition,
  LibraryViewLayout,
  LibView,
} from '@/types/shared'
import type { CommunitySeed } from '@/components/collections/sampleLibrary'
import {
  captureLibraryTransportOwner,
  enqueueLibraryCommand,
  enqueueLibraryCommandWithUploads,
  fetchLibrarySnapshot,
  fetchProjectScopedLibrarySnapshot,
  getPendingLibraryCommands,
  getPendingLibraryRows,
  hasLibraryTransportAccount,
  isLibraryQueueBusy,
  isLibraryTransportOwnerCurrent,
  isLibraryTransportEnabled,
  isLibraryWorkspaceAccessDenied,
  libraryWorkspaceDenialReason,
  registerLibraryStoreBridge,
  type LibraryAccessDenialReason,
  type LibraryDurableUpload,
  type LibraryTransportOwner,
  type PendingLibraryCommand,
  type LibraryUploadRequest,
} from './libraryTransport'
import { emitLibrarySaveFailedNotice } from '@/lib/floatingLeftPanelEvents'
import { captureSentryError } from '@/lib/sentry'
import { isImageFile, normalizeImageFile } from '@/lib/uploadImage'
import { getVideoMimeType } from '@/lib/uploadVideo'
import { getActiveGhostSession, getActiveLibraryGhostSession } from './ghostModeStore'
// All trackLibrary* functions are ghost-guarded inside @/lib/analytics — a
// ghost session targeting the event's workspace emits nothing, from any call
// site (this store or UI components).
import {
  trackLibraryAssetEdited,
  trackLibraryAssetsAdded,
  trackLibraryCollectionCreated,
  trackLibraryCollectionEdited,
  trackLibraryFolderEdited,
  trackLibraryPackImported,
  trackLibraryProjectLinkChanged,
  trackLibraryPropertyCreated,
  trackLibraryPropertyEdited,
  trackLibraryPropertyValueSet,
  trackLibrarySavedViewEdited,
  trackLibrarySnapshot,
} from '@/lib/analytics'

// --- Wire shapes (spec §3) — canonical in @/types/shared, re-exported here so
// the ~20 consumer files keep importing from the store.

export type AssetKind = LibraryAssetKind
export type Property = LibraryProperty
export type PropertyOption = LibraryPropertyOption
export type ViewDefinition = LibraryViewDefinition
export type ViewLayout = LibraryViewLayout
export type { LibAsset, LibAssetVersion, LibCollection, LibView, LibraryCommand }

type UndoEntry = { label: string; command: LibraryCommand; inverse: LibraryCommand }

export function shouldApplyUploadCompletion(
  asset: Pick<LibAsset, 'blobKey' | 'deletedAt'> | undefined,
  expectedBlobKey: string | undefined,
): boolean {
  if (!asset || asset.deletedAt) return false
  return expectedBlobKey === undefined || asset.blobKey === expectedBlobKey
}

const LEGACY_VIDEO_FILE_RE = /\.(?:mp4|m4v|mov|webm|mkv|avi|ts)(?:$|[?#])/i
const LEGACY_AUDIO_FILE_RE = /\.(?:mp3|m4a|aac|wav|flac|ogg|opus)(?:$|[?#])/i

/** Before canvas captures used the canonical media classifier, uploaded videos
    could be persisted as `kind: image`. The immutable media URL/filename still
    tells us what the row is, so repair that one contradictory legacy shape at
    the snapshot boundary. Explicit non-image kinds always win. */
export function recoverLegacyLibraryMediaKind(
  asset: Pick<LibAsset | LibAssetVersion, 'kind' | 'src' | 'originalFilename'>,
): LibraryAssetKind {
  if (asset.kind !== 'image') return asset.kind
  const candidates = [asset.originalFilename, asset.src].filter((value): value is string => !!value)
  if (candidates.some(value => LEGACY_VIDEO_FILE_RE.test(value))) return 'video'
  if (candidates.some(value => LEGACY_AUDIO_FILE_RE.test(value))) return 'audio'
  return asset.kind
}

export function recoverLegacyLibrarySnapshotMedia(snapshot: LibrarySnapshot): LibrarySnapshot {
  let changed = false
  const assets = snapshot.assets.map(asset => {
    const kind = recoverLegacyLibraryMediaKind(asset)
    if (kind === asset.kind) return asset
    changed = true
    return { ...asset, kind }
  })
  const versions = snapshot.versions.map(version => {
    const kind = recoverLegacyLibraryMediaKind(version)
    if (kind === version.kind) return version
    changed = true
    return { ...version, kind }
  })
  return changed ? { ...snapshot, assets, versions } : snapshot
}

/** Finder and some browsers provide an empty/generic MIME for MOV/MKV/etc.
    Repair it from the canonical extension map before IndexedDB, poster capture,
    and backend upload init all consume the same File. */
export function normalizeLibraryVideoFile(file: File): File {
  if (!isSupportedLibraryVideoFile(file)) return file
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
  const inferred = Object.entries(SUPPORTED_VIDEO_FORMATS).find(([, format]) => format.extension === extension)?.[0]
  const contentType = inferred ?? getVideoMimeType(file)
  if (file.type === contentType) return file
  return new File([file], file.name, { type: contentType, lastModified: file.lastModified })
}

/** Library upload init accepts exactly the shared canonical containers. An
    extension match repairs empty/generic MIME; an unsupported video/* value
    such as video/ogg is not enough on its own. */
export function isSupportedLibraryVideoFile(file: File): boolean {
  const dot = file.name.lastIndexOf('.')
  if (dot >= 0) {
    const extension = file.name.toLowerCase().slice(dot)
    return Object.values(SUPPORTED_VIDEO_FORMATS).some(format => format.extension === extension)
  }
  return Object.hasOwn(SUPPORTED_VIDEO_FORMATS, file.type)
}

export function mergePendingRows(
  snapshot: LibrarySnapshot,
  local: Pick<LibraryState, 'collections' | 'assets' | 'versions' | 'projectLinks'>,
  pending: ReturnType<typeof getPendingLibraryRows>,
): LibrarySnapshot {
  if (!pending.assetIds.size && !pending.collectionIds.size && !pending.projectIds.size) return snapshot

  const localAssets = local.assets.filter(asset => pending.assetIds.has(asset.id) && !asset.deletedAt)
  const localCollections = local.collections.filter(
    collection => pending.collectionIds.has(collection.id) && !collection.deletedAt,
  )
  const localVersions = local.versions.filter(version => pending.assetIds.has(version.assetId))

  return {
    ...snapshot,
    collections: [
      ...snapshot.collections.filter(collection => !pending.collectionIds.has(collection.id)),
      ...localCollections,
    ],
    assets: [...snapshot.assets.filter(asset => !pending.assetIds.has(asset.id)), ...localAssets],
    versions: [
      ...(snapshot.versions ?? []).filter(version => !pending.assetIds.has(version.assetId)),
      ...localVersions,
    ],
    projectLinks: {
      ...snapshot.projectLinks,
      ...Object.fromEntries([...pending.projectIds].map(projectId => [projectId, local.projectLinks[projectId] ?? []])),
    },
  }
}

/** Union pending command snapshots captured before and after a fetch. Commands
    present before the request must still replay when their ack races the GET;
    commands enqueued during the request append in FIFO order. If an upload
    materialized meanwhile, prefer its newer https-backed command for that id. */
export function unionPendingLibraryCommands(
  before: PendingLibraryCommand[],
  after: PendingLibraryCommand[],
): PendingLibraryCommand[] {
  const afterById = new Map(after.map(item => [item.id, item]))
  const seen = new Set<string>()
  const merged = before.map(item => {
    seen.add(item.id)
    return afterById.get(item.id) ?? item
  })
  for (const item of after) {
    if (!seen.has(item.id)) merged.push(item)
  }
  return merged
}

/** Replay the complete optimistic command log over a server snapshot. Library
    commands are value-complete and use the same pure executor as dispatch, so
    this protects renames, deletes, moves, metadata, properties, views and links
    as well as creates — including when the visible store currently belongs to
    a different workspace. */
export function replayPendingLibraryCommands(
  snapshot: LibrarySnapshot,
  pending: PendingLibraryCommand[],
): LibrarySnapshot {
  if (!pending.length) return snapshot
  let working = {
    ...snapshot,
    versions: snapshot.versions ?? [],
    hydrated: true,
    undoDepth: 0,
    redoDepth: 0,
  } as unknown as LibraryState
  for (const { command } of pending) {
    const result = execute(working, command)
    if (result) working = { ...working, ...result[0] }
  }
  return {
    ...snapshot,
    collections: working.collections,
    assets: working.assets,
    versions: working.versions,
    folders: working.folders,
    tagVocab: working.tagVocab,
    properties: working.properties,
    views: working.views,
    projectLinks: working.projectLinks,
  }
}

// --- Store state ---------------------------------------------------------------

export type LibraryState = {
  hydrated: boolean
  /** The backend refused this workspace: no rows, no writes. */
  accessDenied: boolean
  /** Which refusal it was, so the UI can prompt correctly — a guest needs a
      sign-up, a non-member needs a workspace switch. Null unless accessDenied. */
  accessDeniedReason: LibraryAccessDenialReason | null
  /**
   * The last snapshot fetch failed for an operational reason (transport blip,
   * 5xx) rather than an authoritative refusal. Rows shown may be stale or
   * empty, so the UI offers a retry instead of reading as "your library is
   * empty". Distinct from `accessDenied`, which is the server's real answer.
   */
  loadFailed: boolean
  /**
   * A `retryLoad` refetch is in flight. The failure notice keys off this to
   * stay mounted (with its spinner) through the window where the retry has
   * cleared `loadFailed` but the outcome isn't known yet.
   */
  retrying: boolean
  /**
   * Set when the store holds a PROJECT-SCOPED view for a link guest: the user
   * can open the project but is not an org member, so they see and use exactly
   * the collections added to this project — read-only, vault invisible.
   */
  guestProjectScope: string | null
  /** Which workspace this store instance currently shows ('personal' | orgId). */
  workspaceId: string
  collections: LibCollection[]
  assets: LibAsset[]
  /** Append-only media history per asset (mostly one row each). */
  versions: LibAssetVersion[]
  folders: string[]
  tagVocab: string[]
  properties: Property[]
  views: LibView[]
  /** Per-project enablement: projectId → collection ids that project can reference. */
  projectLinks: Record<string, string[]>
  /** created_by uuid → profile alias (from the snapshot), for attribution rows. */
  members: Record<string, string>
  /** Bumped on every undo/redo-stack change so UI can subscribe. */
  undoDepth: number
  redoDepth: number

  /** Point the store at a workspace; re-hydrates from that workspace's snapshot.
   *  Pass the current project so a vault-denied user with project access can
   *  fall back to the project-scoped guest view. */
  setWorkspace: (workspaceId: string, context?: LibraryWorkspaceContext) => Promise<void>
  /** Clear visible rows and cancel hydration while access to a workspace is unknown. */
  invalidateWorkspace: (workspaceId: string) => void
  /** Re-run the current workspace's snapshot fetch after a `loadFailed` load.
   *  Rows stay in the store while it retries (hydrated-gated surfaces show
   *  their loading state), so a second failure restores them, not a wipe. */
  retryLoad: () => Promise<void>
  hydrate: () => Promise<void>
  resetAll: () => Promise<void>
  /** Copy the selected community packs (root folders) into this workspace.
      Collections already here (by id) are skipped, so packs import one at a
      time and can be revisited for ones added since. */
  importCommunityLibrary: (seed: CommunitySeed, roots: string[]) => void

  /** THE seam: every mutation goes through here (spec §5.2/§5.4).
      ship:false = apply+record locally but let the caller own transport
      (upload flows enqueue a materialize-when-ready entry instead). */
  dispatch: (command: LibraryCommand, opts?: { label?: string; record?: boolean; ship?: boolean }) => void
  undo: () => string | null
  redo: () => string | null

  importFiles: (files: File[], collectionId: string | null) => Promise<string>
}

// --- IndexedDB (hand-rolled, no deps) ----------------------------------------

const DB_NAME = 'martini-library-sandbox'
const STORE = 'kv'

function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await idb()
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE).objectStore(STORE).get(key)
    req.onsuccess = () => resolve(req.result as T | undefined)
    req.onerror = () => reject(req.error)
  })
}

async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await idb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(value, key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

async function idbSetMany(entries: Array<{ key: string; value: unknown }>): Promise<void> {
  if (!entries.length) return
  const db = await idb()
  return new Promise((resolve, reject) => {
    // One transaction is atomic: a reload/abort while any put is pending
    // commits either every selected source blob or none of them.
    const tx = db.transaction(STORE, 'readwrite')
    const objectStore = tx.objectStore(STORE)
    for (const entry of entries) objectStore.put(entry.value, entry.key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error ?? new Error('Library blob transaction aborted'))
  })
}

async function idbDelete(key: string): Promise<void> {
  const db = await idb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).delete(key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

async function idbDeleteMany(keys: string[]): Promise<void> {
  const unique = [...new Set(keys)]
  if (!unique.length) return
  const db = await idb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    const objectStore = tx.objectStore(STORE)
    for (const key of unique) objectStore.delete(key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error ?? new Error('Library blob cleanup transaction aborted'))
  })
}

function accountBlobStorageKey(key: string, accountId?: string): string {
  return accountId ? `account:${accountId}:${key}` : key
}

/** Remove native-file staging that never became an owned outbox entry. This is
    used when an auth/workspace transition wins a race with normalization,
    poster capture, or probing. */
async function discardStagedLibraryBlobs(keys: string[], accountId?: string): Promise<void> {
  const allKeys = keys.flatMap(key => [key, `poster:${key}`]).map(key => accountBlobStorageKey(key, accountId))
  try {
    await idbDeleteMany(allKeys)
  } catch (error) {
    captureSentryError(error, {
      operation: 'library_staged_blob_cleanup',
      extra: { count: keys.length },
    })
  }
  // An account transition already cleared the outgoing realm's cache. Do not
  // revoke a same-id object URL the new account may have installed meanwhile.
  if (!accountId || captureLibraryTransportOwner()?.accountId === accountId) {
    for (const key of keys) revokeCachedAssetUrls(key)
  }
}

function libraryAsyncOwnerIsCurrent(owner: LibraryTransportOwner | null): boolean {
  return !isLibraryTransportEnabled() || isLibraryTransportOwnerCurrent(owner)
}

// --- Upload blob → object URL cache -------------------------------------------

/** Object URLs intended for `<img>` surfaces (original images or video posters). */
const urlCache = new Map<string, string>()
/** Object URLs for the original local blob (video/audio playback and downloads). */
const mediaUrlCache = new Map<string, string>()

const POSTERLESS_MEDIA_THUMBNAIL = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 90"><rect width="160" height="90" fill="#191919"/><rect x="1" y="1" width="158" height="88" rx="5" fill="none" stroke="#3f3f3f"/><path d="M70 31.5 96 45 70 58.5Z" fill="#a3a3a3"/></svg>',
)}`

export function resolveLibraryAssetDisplayUrl(
  asset: Pick<LibAsset, 'src' | 'id' | 'srcLarge' | 'blobKey' | 'posterSrc' | 'kind'>,
  cachedDisplayUrl: string | undefined,
  size: 'grid' | 'large' = 'grid',
): string {
  if (asset.src.startsWith('idb:')) {
    if (asset.kind === 'image') return cachedDisplayUrl ?? ''
    return cachedDisplayUrl ?? POSTERLESS_MEDIA_THUMBNAIL
  }
  // Non-image media carries a display still — <img> surfaces can't render mp4s.
  if (asset.posterSrc) return asset.posterSrc
  // Historical misclassified uploads have no poster. Once their kind is
  // recovered at hydration, show a deliberate media tile instead of asking
  // every <img> surface to decode the raw mp4/audio URL.
  if (asset.kind === 'video' || asset.kind === 'audio' || asset.kind === 'model3d') {
    return POSTERLESS_MEDIA_THUMBNAIL
  }
  return size === 'large' && asset.srcLarge ? asset.srcLarge : asset.src
}

/** Resolve an asset's displayable URL (object URL for uploads, passthrough otherwise). */
export function getAssetUrl(
  asset: Pick<LibAsset, 'src' | 'id' | 'srcLarge' | 'blobKey' | 'posterSrc' | 'kind'>,
  size: 'grid' | 'large' = 'grid',
): string {
  const key = asset.blobKey ?? `blob:${asset.id}`
  return resolveLibraryAssetDisplayUrl(asset, urlCache.get(key), size)
}

export function resolveLibraryAssetMediaUrl(
  asset: Pick<LibAsset, 'src' | 'id' | 'srcLarge' | 'blobKey' | 'kind'>,
  cachedMediaUrl: string | undefined,
  size: 'grid' | 'large' = 'large',
): string {
  if (asset.src.startsWith('idb:')) return cachedMediaUrl ?? ''
  if (asset.kind === 'image' && size === 'large' && asset.srcLarge) return asset.srcLarge
  return asset.src
}

/** Original bytes for playback/export; never substitutes a captured poster. */
export function getAssetMediaUrl(
  asset: Pick<LibAsset, 'src' | 'id' | 'srcLarge' | 'blobKey' | 'kind'>,
  size: 'grid' | 'large' = 'large',
): string {
  const key = asset.blobKey ?? `blob:${asset.id}`
  return resolveLibraryAssetMediaUrl(asset, mediaUrlCache.get(key), size)
}

function revokeCachedAssetUrls(key: string): void {
  const urls = new Set([urlCache.get(key), mediaUrlCache.get(key)].filter((url): url is string => !!url))
  for (const url of urls) URL.revokeObjectURL(url)
  urlCache.delete(key)
  mediaUrlCache.delete(key)
}

function clearCachedAssetUrls(): void {
  const urls = new Set([...urlCache.values(), ...mediaUrlCache.values()])
  for (const url of urls) URL.revokeObjectURL(url)
  urlCache.clear()
  mediaUrlCache.clear()
}

async function cacheBlobUrl(asset: Pick<LibAsset, 'id' | 'blobKey' | 'kind'>): Promise<void> {
  const key = asset.blobKey ?? `blob:${asset.id}`
  if (mediaUrlCache.has(key)) return
  const owner = captureLibraryTransportOwner()
  const accountId = owner?.accountId
  // The original blob and poster have distinct jobs. Loading a poster into the
  // raw-media cache makes a just-imported video "play" as a jpeg; loading the
  // original video into the display cache makes every tile a broken <img>.
  const [blob, poster] = await Promise.all([
    idbGet<Blob>(accountBlobStorageKey(key, accountId)),
    idbGet<Blob>(accountBlobStorageKey(`poster:${key}`, accountId)).then(
      value => value ?? idbGet<Blob>(accountBlobStorageKey(`blob:poster:${asset.id}`, accountId)),
    ),
  ])
  if (isLibraryTransportEnabled() && !isLibraryTransportOwnerCurrent(owner)) return
  if (blob) {
    const rawUrl = URL.createObjectURL(blob)
    mediaUrlCache.set(key, rawUrl)
    if (asset.kind === 'image' && !poster) urlCache.set(key, rawUrl)
  }
  if (poster) urlCache.set(key, URL.createObjectURL(poster))
}

/** First-frame poster for a video file; null when the codec defeats us (the
    caller keeps raw playback separate and display surfaces use a neutral tile). */
function capturePoster(file: File): Promise<Blob | null> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.preload = 'metadata'
    const done = (blob: Blob | null) => {
      URL.revokeObjectURL(url)
      resolve(blob)
    }
    const bail = setTimeout(() => done(null), 8000)
    video.onerror = () => {
      clearTimeout(bail)
      done(null)
    }
    video.onloadeddata = () => {
      // a beat past zero: frame 0 is often black in generated footage
      video.currentTime = Math.min(0.1, (video.duration || 1) / 10)
    }
    video.onseeked = () => {
      clearTimeout(bail)
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (!ctx || !canvas.width) return done(null)
      ctx.drawImage(video, 0, 0)
      canvas.toBlob(b => done(b), 'image/jpeg', 0.85)
    }
    video.src = url
  })
}

/** Persist the source bytes before any slow metadata/poster work. Upload
    outbox descriptors can safely point at this key as soon as it resolves. */
async function putAssetSourceBlobAt(key: string, file: File, accountId?: string): Promise<void> {
  await putAssetSourceBlobsAtomically([{ key, file }], accountId)
}

/** Atomically persist a whole native selection before its single optimistic
    batch is dispatched. Cache setup is best-effort and synchronous after the
    transaction commits, leaving no partially-durable multi-file window. */
export async function putAssetSourceBlobsAtomically(
  entries: Array<{ key: string; file: File }>,
  accountId?: string,
): Promise<void> {
  await idbSetMany(entries.map(entry => ({ key: accountBlobStorageKey(entry.key, accountId), value: entry.file })))
  for (const { key, file } of entries) {
    try {
      if (accountId && captureLibraryTransportOwner()?.accountId !== accountId) continue
      revokeCachedAssetUrls(key)
      const rawUrl = URL.createObjectURL(file)
      mediaUrlCache.set(key, rawUrl)
      if (!isSupportedLibraryVideoFile(file)) urlCache.set(key, rawUrl)
    } catch (error) {
      captureSentryError(error, {
        operation: 'library_blob_url_cache',
        extra: { key, fileName: file.name },
      })
    }
  }
}

/** Store a file's blob (+ poster for videos) under `key` and register the
    displayable object URL under the same key. Returns the captured poster so
    upload flows can ship it alongside the media. */
export async function putAssetBlobAt(key: string, file: File, accountId?: string): Promise<Blob | null> {
  await putAssetSourceBlobAt(key, file, accountId)
  if (isSupportedLibraryVideoFile(file)) {
    const poster = await capturePoster(file)
    if (poster) {
      await idbSet(accountBlobStorageKey(`poster:${key}`, accountId), poster)
      if (!accountId || captureLibraryTransportOwner()?.accountId === accountId) {
        urlCache.set(key, URL.createObjectURL(poster))
      }
      return poster
    }
    return null
  }
  return null
}

/** Width/height of an image or video file (best effort, null on failure). */
export function probeMediaSize(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file)
    const done = (v: { width: number; height: number } | null) => {
      URL.revokeObjectURL(url)
      resolve(v)
    }
    if (isSupportedLibraryVideoFile(file)) {
      const v = document.createElement('video')
      v.preload = 'metadata'
      v.onloadedmetadata = () => done(v.videoWidth ? { width: v.videoWidth, height: v.videoHeight } : null)
      v.onerror = () => done(null)
      v.src = url
    } else {
      const img = new Image()
      img.onload = () => done({ width: img.naturalWidth, height: img.naturalHeight })
      img.onerror = () => done(null)
      img.src = url
    }
  })
}

// --- Row-set keys (the content the server owns) ---------------------------------

type Snapshot = Pick<
  LibraryState,
  'collections' | 'assets' | 'versions' | 'folders' | 'tagVocab' | 'properties' | 'views' | 'projectLinks' | 'members'
>

const EMPTY_ROWS: Snapshot = {
  collections: [],
  assets: [],
  versions: [],
  folders: [],
  tagVocab: [],
  properties: [],
  views: [],
  projectLinks: {},
  members: {},
}

// --- Ids, slugs ----------------------------------------------------------------

export const uid = () => crypto.randomUUID().slice(0, 8)

/** Stable kebab slug, deduped against existing (spec §7 naming contract). */
export function toSlug(name: string, taken: string[]): string {
  const base =
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'untitled'
  let slug = base
  for (let n = 2; taken.includes(slug); n++) slug = `${base}-${n}`
  return slug
}

export function toPropertyKey(label: string, taken: string[]): string {
  const base =
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'property'
  let key = base
  for (let n = 2; taken.includes(key); n++) key = `${base}_${n}`
  return key
}

// --- Command executor -----------------------------------------------------------
// Every command returns [patch, inverse]. Inverses are value-complete (spec
// §5.4): computable from state the client already has, so undo is a pure
// re-dispatch through the same seam.

function touch<T extends { rev: number; updatedAt: number }>(row: T): T {
  return { ...row, rev: row.rev + 1, updatedAt: Date.now() }
}

export function execute(state: LibraryState, cmd: LibraryCommand): [Partial<LibraryState>, LibraryCommand] | null {
  switch (cmd.type) {
    case 'collection.create': {
      // Ids are stable forever: creating an id that already exists (e.g.
      // re-importing deterministic sample rows after an undo) revives/
      // replaces that row — never appends a duplicate sibling.
      return [
        { collections: [...state.collections.filter(c => c.id !== cmd.collection.id), cmd.collection] },
        { type: 'collection.delete', id: cmd.collection.id },
      ]
    }
    case 'collection.rename': {
      const prev = state.collections.find(c => c.id === cmd.id)
      if (!prev || prev.name === cmd.name) return null
      return [
        { collections: state.collections.map(c => (c.id === cmd.id ? touch({ ...c, name: cmd.name }) : c)) },
        { type: 'collection.rename', id: cmd.id, name: prev.name },
      ]
    }
    case 'collection.delete': {
      // Soft: the collection AND its live assets get deletedAt; restore
      // brings back exactly the rows this delete hid (same ids, spec §5.4).
      const now = Date.now()
      const doomedAssets = state.assets.filter(a => a.collectionId === cmd.id && !a.deletedAt).map(a => a.id)
      return [
        {
          collections: state.collections.map(c => (c.id === cmd.id ? touch({ ...c, deletedAt: now }) : c)),
          assets: state.assets.map(a => (doomedAssets.includes(a.id) ? touch({ ...a, deletedAt: now }) : a)),
        },
        {
          type: 'batch',
          label: 'restore collection',
          commands: [
            { type: 'collection.restore', id: cmd.id },
            { type: 'asset.restore', ids: doomedAssets },
          ],
        },
      ]
    }
    case 'collection.restore': {
      return [
        {
          collections: state.collections.map(c => (c.id === cmd.id ? touch({ ...c, deletedAt: undefined }) : c)),
        },
        { type: 'collection.delete', id: cmd.id },
      ]
    }
    case 'collection.move': {
      const prev = state.collections.find(c => c.id === cmd.id)
      if (!prev || prev.folder === cmd.folder) return null
      return [
        { collections: state.collections.map(c => (c.id === cmd.id ? touch({ ...c, folder: cmd.folder }) : c)) },
        { type: 'collection.move', id: cmd.id, folder: prev.folder },
      ]
    }
    case 'collection.setTags': {
      const prev = state.collections.find(c => c.id === cmd.id)
      if (!prev) return null
      return [
        {
          collections: state.collections.map(c => (c.id === cmd.id ? touch({ ...c, tags: cmd.tags }) : c)),
          tagVocab: [...new Set([...state.tagVocab, ...cmd.tags])],
        },
        { type: 'collection.setTags', id: cmd.id, tags: prev.tags },
      ]
    }
    case 'collection.setHero': {
      const prev = state.collections.find(c => c.id === cmd.id)
      if (!prev) return null
      return [
        {
          collections: state.collections.map(c => (c.id === cmd.id ? touch({ ...c, heroAssetId: cmd.assetId }) : c)),
        },
        { type: 'collection.setHero', id: cmd.id, assetId: prev.heroAssetId },
      ]
    }
    case 'collection.setDescription': {
      const prev = state.collections.find(c => c.id === cmd.id)
      if (!prev || (prev.description ?? '') === cmd.description) return null
      return [
        {
          collections: state.collections.map(c =>
            c.id === cmd.id ? touch({ ...c, description: cmd.description }) : c,
          ),
        },
        { type: 'collection.setDescription', id: cmd.id, description: prev.description ?? '' },
      ]
    }
    case 'folder.create': {
      const path = cmd.path.trim()
      if (!path || state.folders.includes(path)) return null
      return [{ folders: [...state.folders, path] }, { type: 'folder.delete', path }]
    }
    case 'folder.rename': {
      // Renaming onto an existing folder would be a MERGE — its inverse cannot
      // be expressed as a symmetric rename (undo would relocate rows that were
      // already in the target and delete the folder). v1 disallows it.
      if (state.folders.some(f => f === cmd.next || f.startsWith(`${cmd.next}/`))) return null
      const swap = (p: string | null) =>
        p === cmd.path ? cmd.next : p?.startsWith(`${cmd.path}/`) ? cmd.next + p.slice(cmd.path.length) : p
      return [
        {
          folders: state.folders.map(f => swap(f) as string),
          collections: state.collections.map(c =>
            swap(c.folder) === c.folder ? c : touch({ ...c, folder: swap(c.folder) }),
          ),
        },
        { type: 'folder.rename', path: cmd.next, next: cmd.path },
      ]
    }
    case 'folder.delete': {
      // Value-complete inverse: remember where each collection lived.
      const displaced: Record<string, string | null> = {}
      for (const c of state.collections) {
        if (c.folder === cmd.path || c.folder?.startsWith(`${cmd.path}/`)) displaced[c.id] = c.folder
      }
      const removed = state.folders.filter(f => f === cmd.path || f.startsWith(`${cmd.path}/`))
      return [
        {
          folders: state.folders.filter(f => !removed.includes(f)),
          collections: state.collections.map(c => (c.id in displaced ? touch({ ...c, folder: null }) : c)),
        },
        {
          type: 'batch',
          label: 'restore folder',
          commands: [
            ...removed.map(path => ({ type: 'folder.create', path }) as LibraryCommand),
            ...Object.entries(displaced).map(
              ([id, folder]) => ({ type: 'collection.move', id, folder }) as LibraryCommand,
            ),
          ],
        },
      ]
    }
    case 'asset.create': {
      // Mirror the server: every asset gets its deterministic history root.
      const vid = cmd.asset.currentVersionId ?? `${cmd.asset.id}-v1`
      const asset: LibAsset = { ...cmd.asset, currentVersionId: vid }
      const hasHistory = state.versions.some(v => v.assetId === asset.id)
      const v1: LibAssetVersion = {
        id: vid,
        assetId: asset.id,
        versionNumber: 1,
        kind: asset.kind,
        src: asset.src,
        srcLarge: asset.srcLarge,
        posterSrc: asset.posterSrc,
        r2Key: asset.r2Key,
        posterR2Key: asset.posterR2Key,
        originalFilename: asset.originalFilename,
        width: asset.width,
        height: asset.height,
        sizeBytes: asset.sizeBytes,
        source: asset.sourceKind ?? 'initial',
        createdAt: asset.createdAt,
      }
      return [
        {
          assets: [...state.assets.filter(a => a.id !== asset.id), asset],
          ...(hasHistory ? {} : { versions: [...state.versions, v1] }),
        },
        { type: 'asset.delete', ids: [asset.id] },
      ]
    }
    case 'asset.rename': {
      const prev = state.assets.find(a => a.id === cmd.id)
      if (!prev || prev.name === cmd.name) return null
      return [
        { assets: state.assets.map(a => (a.id === cmd.id ? touch({ ...a, name: cmd.name }) : a)) },
        { type: 'asset.rename', id: cmd.id, name: prev.name },
      ]
    }
    case 'asset.move': {
      const cameFrom: Record<string, string> = {}
      for (const a of state.assets) if (cmd.ids.includes(a.id)) cameFrom[a.id] = a.collectionId
      const targetFor = (id: string) => cmd.perAssetTarget?.[id] ?? cmd.collectionId
      if (cmd.ids.every(id => cameFrom[id] === targetFor(id))) return null
      return [
        {
          assets: state.assets.map(a => (cmd.ids.includes(a.id) ? touch({ ...a, collectionId: targetFor(a.id) }) : a)),
        },
        { type: 'asset.move', ids: cmd.ids, collectionId: cmd.collectionId, perAssetTarget: cameFrom },
      ]
    }
    case 'asset.delete': {
      // Soft; blobs stay (undo needs them). resetAll purges the sandbox.
      const now = Date.now()
      return [
        { assets: state.assets.map(a => (cmd.ids.includes(a.id) ? touch({ ...a, deletedAt: now }) : a)) },
        { type: 'asset.restore', ids: cmd.ids },
      ]
    }
    case 'asset.restore': {
      return [
        { assets: state.assets.map(a => (cmd.ids.includes(a.id) ? touch({ ...a, deletedAt: undefined }) : a)) },
        { type: 'asset.delete', ids: cmd.ids },
      ]
    }
    case 'asset.update': {
      const prev = state.assets.find(a => a.id === cmd.id)
      if (!prev) return null
      const keys = Object.keys(cmd.patch) as (keyof typeof cmd.patch)[]
      const before: Record<string, unknown> = {}
      for (const k of keys) before[k] = prev[k] ?? null
      // null/undefined in the patch DELETES the key (dev's stale-poster fix) —
      // a replace that drops the poster must not leave the old one behind.
      const next = { ...prev }
      for (const k of keys) {
        const value = cmd.patch[k]
        if (value === null || value === undefined) delete next[k]
        else next[k] = value as never
      }
      // Media replace (versionId present): append to history and move the
      // pointer. Undo is a pointer move back — never a second append. A redo
      // re-dispatches the same command, so an already-known version id only
      // moves the pointer (server parity: upsert ignoreDuplicates).
      if (cmd.versionId && cmd.patch.src) {
        if (state.versions.some(v => v.id === cmd.versionId)) {
          return execute(state, { type: 'asset.setCurrentVersion', id: cmd.id, versionId: cmd.versionId })
        }
        const nextNumber =
          Math.max(0, ...state.versions.filter(v => v.assetId === cmd.id).map(v => v.versionNumber)) + 1
        const version: LibAssetVersion = {
          id: cmd.versionId,
          assetId: cmd.id,
          versionNumber: nextNumber,
          kind: next.kind,
          src: next.src,
          srcLarge: next.srcLarge,
          posterSrc: next.posterSrc,
          r2Key: next.r2Key,
          posterR2Key: next.posterR2Key,
          originalFilename: next.originalFilename,
          width: next.width,
          height: next.height,
          sizeBytes: next.sizeBytes,
          source: cmd.patch.sourceKind === 'generation' ? 'generation' : 'replace',
          createdAt: Date.now(),
        }
        return [
          {
            assets: state.assets.map(a => (a.id === cmd.id ? touch({ ...next, currentVersionId: cmd.versionId }) : a)),
            versions: [...state.versions, version],
          },
          prev.currentVersionId
            ? { type: 'asset.setCurrentVersion', id: cmd.id, versionId: prev.currentVersionId }
            : { type: 'asset.update', id: cmd.id, patch: before as never },
        ]
      }
      return [
        { assets: state.assets.map(a => (a.id === cmd.id ? touch(next) : a)) },
        { type: 'asset.update', id: cmd.id, patch: before as never },
      ]
    }
    case 'asset.setCurrentVersion': {
      const prev = state.assets.find(a => a.id === cmd.id)
      const version = state.versions.find(v => v.id === cmd.versionId && v.assetId === cmd.id)
      if (!prev || !version || prev.currentVersionId === cmd.versionId) return null
      const media: Partial<LibAsset> = {
        kind: version.kind,
        src: version.src,
        srcLarge: version.srcLarge,
        posterSrc: version.posterSrc,
        r2Key: version.r2Key,
        posterR2Key: version.posterR2Key,
        originalFilename: version.originalFilename,
        width: version.width,
        height: version.height,
        sizeBytes: version.sizeBytes,
        blobKey: undefined,
        currentVersionId: version.id,
      }
      const beforeMedia: Record<string, unknown> = {}
      for (const k of Object.keys(media)) beforeMedia[k] = prev[k as keyof LibAsset]
      return [
        { assets: state.assets.map(a => (a.id === cmd.id ? touch({ ...a, ...media }) : a)) },
        prev.currentVersionId
          ? { type: 'asset.setCurrentVersion', id: cmd.id, versionId: prev.currentVersionId }
          : { type: 'asset.update', id: cmd.id, patch: beforeMedia as never },
      ]
    }
    case 'asset.setValue': {
      const prev = state.assets.find(a => a.id === cmd.id)
      if (!prev) return null
      const prevValue = prev.attrs[cmd.key] ?? null
      if (prevValue === cmd.value) return null
      const attrs = { ...prev.attrs }
      if (cmd.value === null) delete attrs[cmd.key]
      else attrs[cmd.key] = cmd.value
      return [
        { assets: state.assets.map(a => (a.id === cmd.id ? touch({ ...a, attrs }) : a)) },
        { type: 'asset.setValue', id: cmd.id, key: cmd.key, value: prevValue },
      ]
    }
    case 'property.create': {
      if (state.properties.some(p => p.key === cmd.property.key && !p.deletedAt)) return null
      return [
        { properties: [...state.properties.filter(p => p.key !== cmd.property.key), cmd.property] },
        { type: 'property.delete', key: cmd.property.key },
      ]
    }
    case 'property.rename': {
      // The user's word only — the key (attrs) never changes (spec §4).
      const prev = state.properties.find(p => p.key === cmd.key && !p.deletedAt)
      if (!prev || prev.label === cmd.label) return null
      return [
        { properties: state.properties.map(p => (p.key === cmd.key ? { ...p, label: cmd.label } : p)) },
        { type: 'property.rename', key: cmd.key, label: prev.label },
      ]
    }
    case 'property.setType': {
      // In-place type change. Attrs and options are deliberately untouched:
      // readers tolerate mismatched leftovers (render raw), options revive if
      // the type returns to select. Inverse restores the prior type + config —
      // so a config-only change must apply, else its own inverse (same type,
      // prior config) would be rejected and undo would silently no-op.
      const prev = state.properties.find(p => p.key === cmd.key && !p.deletedAt)
      if (!prev) return null
      const sameConfig = JSON.stringify(prev.config ?? {}) === JSON.stringify(cmd.config ?? {})
      if (prev.type === cmd.propertyType && sameConfig) return null
      return [
        {
          properties: state.properties.map(p =>
            p.key === cmd.key ? { ...p, type: cmd.propertyType, config: cmd.config } : p,
          ),
        },
        { type: 'property.setType', key: cmd.key, propertyType: prev.type, config: prev.config },
      ]
    }
    case 'property.delete': {
      // Soft: attrs keep their cells (hidden while the property is deleted);
      // restore brings the column back intact.
      const prev = state.properties.find(p => p.key === cmd.key && !p.deletedAt)
      if (!prev) return null
      return [
        { properties: state.properties.map(p => (p.key === cmd.key ? { ...p, deletedAt: Date.now() } : p)) },
        { type: 'property.restore', key: cmd.key },
      ]
    }
    case 'property.restore': {
      return [
        { properties: state.properties.map(p => (p.key === cmd.key ? { ...p, deletedAt: undefined } : p)) },
        { type: 'property.delete', key: cmd.key },
      ]
    }
    case 'property.addOption': {
      const prop = state.properties.find(p => p.key === cmd.key)
      if (!prop || prop.options.some(o => o.id === cmd.option.id || o.value === cmd.option.value)) return null
      return [
        {
          properties: state.properties.map(p =>
            p.key === cmd.key ? { ...p, options: [...p.options, cmd.option] } : p,
          ),
        },
        { type: 'property.removeOption', key: cmd.key, optionId: cmd.option.id },
      ]
    }
    case 'property.removeOption': {
      const prop = state.properties.find(p => p.key === cmd.key)
      const opt = prop?.options.find(o => o.id === cmd.optionId)
      if (!prop || !opt) return null
      return [
        {
          properties: state.properties.map(p =>
            p.key === cmd.key ? { ...p, options: p.options.filter(o => o.id !== cmd.optionId) } : p,
          ),
        },
        { type: 'property.addOption', key: cmd.key, option: opt },
      ]
    }
    case 'property.renameOption': {
      const prop = state.properties.find(p => p.key === cmd.key)
      const opt = prop?.options.find(o => o.id === cmd.optionId)
      if (!prop || !opt || opt.value === cmd.value) return null
      return [
        {
          properties: state.properties.map(p =>
            p.key === cmd.key
              ? { ...p, options: p.options.map(o => (o.id === cmd.optionId ? { ...o, value: cmd.value } : o)) }
              : p,
          ),
        },
        { type: 'property.renameOption', key: cmd.key, optionId: cmd.optionId, value: opt.value },
      ]
    }
    case 'tag.addToVocab': {
      const added = cmd.tags.filter(t => !state.tagVocab.includes(t))
      if (!added.length) return null
      return [
        { tagVocab: [...state.tagVocab, ...added] },
        // Vocabulary growth is additive noise; inverse is a no-op batch.
        { type: 'batch', label: 'noop', commands: [] },
      ]
    }
    case 'view.save': {
      return [
        { views: [...state.views.filter(v => v.id !== cmd.view.id), cmd.view] },
        { type: 'view.delete', id: cmd.view.id },
      ]
    }
    case 'view.update': {
      const prev = state.views.find(v => v.id === cmd.id)
      if (!prev) return null
      return [
        {
          views: state.views.map(v =>
            v.id === cmd.id ? touch({ ...v, name: cmd.name ?? v.name, definition: cmd.definition ?? v.definition }) : v,
          ),
        },
        { type: 'view.update', id: cmd.id, name: prev.name, definition: prev.definition },
      ]
    }
    case 'view.delete': {
      const prev = state.views.find(v => v.id === cmd.id && !v.deletedAt)
      if (!prev) return null
      return [
        { views: state.views.map(v => (v.id === cmd.id ? touch({ ...v, deletedAt: Date.now() }) : v)) },
        { type: 'view.save', view: { ...prev, deletedAt: undefined } },
      ]
    }
    case 'link.setProject': {
      const prev = state.projectLinks[cmd.projectId] ?? []
      const next = [...new Set(cmd.collectionIds)]
      if (prev.length === next.length && prev.every((id, i) => id === next[i])) return null
      return [
        { projectLinks: { ...state.projectLinks, [cmd.projectId]: next } },
        { type: 'link.setProject', projectId: cmd.projectId, collectionIds: prev },
      ]
    }
    case 'batch': {
      // Sequential application; inverse = reversed inverses, one undo entry.
      let working = state
      const patches: Partial<LibraryState>[] = []
      const inverses: LibraryCommand[] = []
      for (const sub of cmd.commands) {
        const result = execute(working, sub)
        if (!result) continue
        const [patch, inverse] = result
        patches.push(patch)
        inverses.push(inverse)
        working = { ...working, ...patch }
      }
      if (!patches.length) return null
      const merged = patches.reduce((acc, p) => ({ ...acc, ...pickLatest(working, p) }), {})
      return [merged, { type: 'batch', label: cmd.label, commands: inverses.reverse() }]
    }
  }
}

/** For batches: the final `working` state already folded every patch; per-key
    we want the LATEST value, which `working` holds. */
function pickLatest(working: LibraryState, patch: Partial<LibraryState>): Partial<LibraryState> {
  const out: Partial<LibraryState> = {}
  for (const k of Object.keys(patch) as (keyof Snapshot)[]) out[k] = working[k] as never
  return out
}

// --- Undo stacks (per workspace, in-memory — spec §5.4) -------------------------

/** Monotonic token: only the LATEST setWorkspace load may commit its snapshot. */
let workspaceLoadToken = 0

const UNDO_CAP = 100
const undoStacks = new Map<string, { undo: UndoEntry[]; redo: UndoEntry[] }>()

function stacksFor(ws: string) {
  let s = undoStacks.get(ws)
  if (!s) {
    s = { undo: [], redo: [] }
    undoStacks.set(ws, s)
  }
  return s
}

export function isGhostReadOnlyWorkspace(workspaceId: string): boolean {
  const librarySession = getActiveLibraryGhostSession()
  if (librarySession?.targetOrgId === workspaceId) return librarySession.mode !== 'write'
  const projectSession = getActiveGhostSession()
  return !!projectSession && projectSession.targetOrgId === workspaceId && projectSession.mode !== 'write'
}

/**
 * One capability contract for every Library write surface. Project guests can
 * read the collections enabled for their project, but only workspace members
 * may change the owning vault. Keeping loading and denial distinct prevents a
 * permission state from being presented as a transient hydration failure.
 */
export type LibraryWriteAccess = 'writable' | 'loading' | 'access-denied' | 'project-read-only' | 'workspace-read-only'

type LibraryWriteAccessState = Pick<LibraryState, 'hydrated' | 'accessDenied' | 'guestProjectScope' | 'workspaceId'>

export function selectLibraryWriteAccess(state: LibraryWriteAccessState): LibraryWriteAccess {
  if (state.guestProjectScope) return 'project-read-only'
  if (state.accessDenied) return 'access-denied'
  if (!state.hydrated) return 'loading'
  if (isGhostReadOnlyWorkspace(state.workspaceId)) return 'workspace-read-only'
  return 'writable'
}

// Stock-level analytics: one library_snapshot per workspace per session, on
// hydration (any surface — the editor panel hydrates too, so this fires for
// active users who never visit /library). Ghost sessions are skipped: an admin
// viewing a customer's library would report that library under their own user.
const snapshotTracked = new Set<string>()
function maybeTrackLibrarySnapshot(
  workspaceId: string,
  snap: {
    collections: LibCollection[]
    assets: LibAsset[]
    versions?: LibAssetVersion[]
    folders: string[]
    tagVocab: string[]
    properties: Property[]
    views: LibView[]
    projectLinks: Record<string, string[]>
  },
): void {
  if (snapshotTracked.has(workspaceId)) return
  // Ghost views skip entirely (the analytics layer drops the event too) —
  // and don't consume the once-per-session slot while ghosting.
  if (getActiveLibraryGhostSession()?.targetOrgId === workspaceId) return
  if (getActiveGhostSession()?.targetOrgId === workspaceId) return
  snapshotTracked.add(workspaceId)
  const assets = snap.assets.filter(a => !a.deletedAt)
  const collections = snap.collections.filter(c => !c.deletedAt)
  const versionsPerAsset = new Map<string, number>()
  for (const v of snap.versions ?? []) versionsPerAsset.set(v.assetId, (versionsPerAsset.get(v.assetId) ?? 0) + 1)
  trackLibrarySnapshot({
    workspaceId,
    collectionCount: collections.length,
    assetCount: assets.length,
    imageCount: assets.filter(a => a.kind === 'image').length,
    videoCount: assets.filter(a => a.kind === 'video').length,
    fragmentCount: assets.filter(a => a.kind === 'text').length,
    assetsWithHistory: [...versionsPerAsset.values()].filter(n => n > 1).length,
    assetsWithValues: assets.filter(a => Object.keys(a.attrs).length > 0).length,
    propertyCount: snap.properties.filter(p => !p.deletedAt).length,
    collectionsWithTags: collections.filter(c => c.tags.length > 0).length,
    tagVocabCount: snap.tagVocab.length,
    linkedProjectCount: Object.values(snap.projectLinks).filter(ids => ids.length > 0).length,
    savedViewCount: snap.views.filter(v => !v.deletedAt).length,
    folderCount: snap.folders.length,
  })
}

// --- Store -----------------------------------------------------------------------

export interface LibraryWorkspaceContext {
  projectId?: string | null
  /** Caller knows the vault isn't theirs (personal-project guest): hydrate the
   *  project scope directly instead of waiting for a denial that never comes. */
  forceProjectScope?: boolean
  /** onAccountReady only: replay the last explicit navigation's context. */
  replayLastContext?: boolean
}

/** Last project context passed for each workspace — the onAccountReady retry
 *  calls setWorkspace without arguments and must not lose the projectId a
 *  link guest's scoped fallback depends on. */
const lastWorkspaceContexts = new Map<string, LibraryWorkspaceContext>()

export const useLibraryStore = create<LibraryState>((set, get) => {
  const persistPatch = (patch: Partial<LibraryState>) => {
    set(patch)
  }

  return {
    hydrated: false,
    accessDenied: false,
    accessDeniedReason: null,
    loadFailed: false,
    retrying: false,
    guestProjectScope: null,
    workspaceId: 'personal',
    collections: [],
    assets: [],
    versions: [],
    folders: [],
    tagVocab: [],
    properties: [],
    views: [],
    projectLinks: {},
    members: {},
    undoDepth: 0,
    redoDepth: 0,

    setWorkspace: async (workspaceId, context) => {
      // Context memory serves exactly one caller: the onAccountReady retry,
      // which replays the pre-auth navigation's context (the projectId a link
      // guest's scoped fallback needs). Every explicit navigation OWNS the
      // memory — a bare vault call (/library page) clears it, so a stale
      // guest scope can never resurrect on a later retry.
      if (context?.replayLastContext) {
        context = lastWorkspaceContexts.get(workspaceId)
      } else if (context?.projectId) {
        lastWorkspaceContexts.set(workspaceId, context)
      } else {
        lastWorkspaceContexts.delete(workspaceId)
      }
      const scopeStale =
        get().guestProjectScope != null
          ? // Scoped → wrong when the requested project differs (or the caller wants the vault).
            get().guestProjectScope !== (context?.projectId ?? null)
          : // Unscoped vault rows → wrong when this navigation demands a project scope.
            !!context?.forceProjectScope
      if (get().workspaceId === workspaceId && get().hydrated && !scopeStale) return
      const beforeLoad = get()
      const canReuseVisibleRows = beforeLoad.workspaceId === workspaceId
      const pendingBefore = getPendingLibraryCommands(workspaceId)
      const token = ++workspaceLoadToken
      set({
        workspaceId,
        hydrated: false,
        accessDenied: false,
        accessDeniedReason: null,
        loadFailed: false,
        guestProjectScope: null,
      })
      // The project panel can mount before AuthContext finishes. Do not turn a
      // missing account into a writable, hydrated empty Library: commands made
      // in that window cannot be owner-bound and would be discarded. The
      // transport's onAccountReady bridge retries this workspace after auth.
      if (isLibraryTransportEnabled() && !hasLibraryTransportAccount()) return
      // Project-scoped guest install, shared by both entry paths: the explicit
      // route (personal project, caller knows the vault isn't theirs) and the
      // deny-latch fallback (team project, vault 403'd).
      const tryInstallProjectScope = async (projectId: string): Promise<boolean> => {
        const scoped = await fetchProjectScopedLibrarySnapshot(workspaceId, projectId)
        if (token !== workspaceLoadToken) return true
        if (!scoped) return false
        const snap = recoverLegacyLibrarySnapshotMedia(scoped)
        set({
          collections: snap.collections,
          assets: snap.assets,
          versions: snap.versions ?? [],
          folders: snap.folders,
          tagVocab: snap.tagVocab,
          properties: snap.properties,
          views: snap.views,
          projectLinks: snap.projectLinks,
          members: snap.members ?? {},
          workspaceId,
          hydrated: true,
          accessDenied: false,
          accessDeniedReason: null,
          loadFailed: false,
          guestProjectScope: projectId,
          undoDepth: 0,
          redoDepth: 0,
        })
        return true
      }
      // A guest on someone's PERSONAL project must not see their own vault in
      // that project's panel — their vault fetch would succeed, so the deny
      // fallback below never fires. The caller says so explicitly instead.
      if (isLibraryTransportEnabled() && context?.forceProjectScope && context.projectId) {
        if (await tryInstallProjectScope(context.projectId)) return
        if (token !== workspaceLoadToken) return
        // Scoped fetch failed (offline / rolling deploy where the endpoint
        // 404s): fail closed AS A GUEST. Falling through would install the
        // guest's own writable vault — the exact mirage this path exists to
        // prevent, with saves landing where the owner can never see them.
        // Empty + scoped keeps every write gate engaged; the next navigation
        // or refresh retries the scoped read.
        set({
          ...EMPTY_ROWS,
          workspaceId,
          hydrated: true,
          accessDenied: false,
          accessDeniedReason: null,
          // The scoped read is the only thing that can populate this view, and
          // it just failed operationally — offer the retry rather than passing
          // an empty guest library off as the truth.
          loadFailed: true,
          guestProjectScope: context.projectId,
          undoDepth: 0,
          redoDepth: 0,
        })
        return
      }
      // The server is the source of truth; local unshipped commands for the
      // outgoing workspace stay queued in the transport (workspace-keyed) and
      // keep shipping in the background.
      const rawSnapshot = await fetchLibrarySnapshot(workspaceId)
      const snap = rawSnapshot ? recoverLegacyLibrarySnapshotMedia(rawSnapshot) : null
      // A newer setWorkspace superseded this load — drop it (last CLICK wins,
      // not last RESOLVE).
      if (token !== workspaceLoadToken) return
      if (snap) {
        const pendingForReplay = () =>
          unionPendingLibraryCommands(pendingBefore, getPendingLibraryCommands(workspaceId))
        // Warm object URLs for rows this browser uploaded but hasn't shipped
        // to https yet (their pixels live only in the local blob cache).
        await Promise.all(
          replayPendingLibraryCommands(snap, pendingForReplay())
            .assets.filter(a => a.src.startsWith('idb:'))
            .map(a => cacheBlobUrl(a)),
        )
        if (token !== workspaceLoadToken) return
        // Upload completion/ACK can race the blob warmup. Recompute from the
        // current queue immediately before install so a now-materialized https
        // row (or an ACKed server row) cannot be overwritten by stale idb: data.
        const reconciled = replayPendingLibraryCommands(snap, pendingForReplay())
        const s = stacksFor(workspaceId)
        // Re-assert workspaceId with the rows: an interleaved older call's
        // set() must never leave rows and id pointing at different workspaces.
        set({
          collections: reconciled.collections,
          assets: reconciled.assets,
          versions: reconciled.versions ?? [],
          folders: reconciled.folders,
          tagVocab: reconciled.tagVocab,
          properties: reconciled.properties,
          views: reconciled.views,
          projectLinks: reconciled.projectLinks,
          members: reconciled.members ?? {},
          workspaceId,
          hydrated: true,
          accessDenied: false,
          accessDeniedReason: null,
          loadFailed: false,
          undoDepth: s.undo.length,
          redoDepth: s.redo.length,
        })
        maybeTrackLibrarySnapshot(workspaceId, reconciled)
      } else {
        // Vault denied but the user can open the current project: fall back to
        // the project-scoped guest view — exactly the collections a member
        // added to this project, read-only. No pending-command replay here:
        // guest writes can never ship, so replaying them would show state the
        // server will never hold (the Clayton mirage).
        if (context?.projectId && isLibraryWorkspaceAccessDenied(workspaceId)) {
          if (await tryInstallProjectScope(context.projectId)) return
        }
        if (isLibraryTransportEnabled() && isLibraryWorkspaceAccessDenied(workspaceId)) {
          // The vault denial is authoritative and the scoped fallback didn't
          // apply (or failed): fail closed. Reinstalling pre-denial rows here
          // would let the mention picker keep browsing a revoked library —
          // selectors gate on `hydrated`, not the panel's accessDenied flag.
          set({
            ...EMPTY_ROWS,
            workspaceId,
            hydrated: true,
            accessDenied: true,
            accessDeniedReason: libraryWorkspaceDenialReason(workspaceId),
            // An authoritative refusal is an answer, not a failed load.
            loadFailed: false,
            undoDepth: 0,
            redoDepth: 0,
          })
          return
        }
        if (isLibraryTransportEnabled()) {
          // An operational GET failure is not an authoritative empty snapshot.
          // Keep a same-owner/same-workspace last-known view, and always replay
          // the durable command log. This is the browser-Back/offline case: an
          // optimistic collection must not disappear merely because its refresh
          // raced a transient network failure.
          const fallback: LibrarySnapshot = {
            workspaceId,
            orgId: '',
            collections: canReuseVisibleRows ? beforeLoad.collections : [],
            assets: canReuseVisibleRows ? beforeLoad.assets : [],
            versions: canReuseVisibleRows ? beforeLoad.versions : [],
            folders: canReuseVisibleRows ? beforeLoad.folders : [],
            tagVocab: canReuseVisibleRows ? beforeLoad.tagVocab : [],
            properties: canReuseVisibleRows ? beforeLoad.properties : [],
            views: canReuseVisibleRows ? beforeLoad.views : [],
            projectLinks: canReuseVisibleRows ? beforeLoad.projectLinks : {},
            members: canReuseVisibleRows ? beforeLoad.members : {},
          }
          const pendingForReplay = () =>
            unionPendingLibraryCommands(pendingBefore, getPendingLibraryCommands(workspaceId))
          await Promise.all(
            replayPendingLibraryCommands(fallback, pendingForReplay())
              .assets.filter(a => a.src.startsWith('idb:'))
              .map(a => cacheBlobUrl(a)),
          )
          if (token !== workspaceLoadToken) return
          const reconciled = replayPendingLibraryCommands(fallback, pendingForReplay())
          const stacks = stacksFor(workspaceId)
          set({
            collections: reconciled.collections,
            assets: reconciled.assets,
            versions: reconciled.versions,
            folders: reconciled.folders,
            tagVocab: reconciled.tagVocab,
            properties: reconciled.properties,
            views: reconciled.views,
            projectLinks: reconciled.projectLinks,
            members: reconciled.members ?? {},
            workspaceId,
            hydrated: true,
            // The rows above are last-known or empty, not the server's answer.
            // Say so, so the surface can offer a retry instead of reading as
            // "your library is empty".
            loadFailed: true,
            undoDepth: stacks.undo.length,
            redoDepth: stacks.redo.length,
          })
          return
        }
        // Transport disabled (tests/SSR): there is no server-backed state.
        set({
          ...EMPTY_ROWS,
          workspaceId,
          hydrated: true,
          accessDenied: isLibraryWorkspaceAccessDenied(workspaceId),
          accessDeniedReason: libraryWorkspaceDenialReason(workspaceId),
          undoDepth: stacksFor(workspaceId).undo.length,
          redoDepth: stacksFor(workspaceId).redo.length,
        })
      }
    },

    invalidateWorkspace: workspaceId => {
      workspaceLoadToken += 1
      set({
        ...EMPTY_ROWS,
        workspaceId,
        hydrated: false,
        loadFailed: false,
        undoDepth: 0,
        redoDepth: 0,
      })
    },

    retryLoad: async () => {
      const { workspaceId } = get()
      // setWorkspace short-circuits on an already-hydrated workspace, so drop
      // the flag it gates on. Rows stay in the store while the refetch runs —
      // clearing them would make a second failure look like a wipe. `retrying`
      // keeps the failure notice mounted through the unhydrated window.
      set({ retrying: true, hydrated: false, loadFailed: false })
      // The call below claims the next load token synchronously (no await
      // before the increment). Anything that bumps the token past that —
      // invalidateWorkspace on a membership change, a newer navigation — has
      // taken ownership of the store, and the fallback must not override it.
      const token = workspaceLoadToken + 1
      try {
        await get().setWorkspace(workspaceId, { replayLastContext: true })
        // setWorkspace can bail before hydrating (no bound account yet).
        // Leaving the store unhydrated would strand every selector that gates
        // on it, so put the surface back exactly where the retry found it.
        if (token === workspaceLoadToken && !get().hydrated && get().workspaceId === workspaceId) {
          set({ hydrated: true, loadFailed: true })
        }
      } finally {
        set({ retrying: false })
      }
    },

    hydrate: async () => {
      if (get().hydrated || typeof window === 'undefined') return
      await get().setWorkspace(get().workspaceId)
    },

    resetAll: async () => {
      // Dev escape hatch: clears LOCAL state only (blob cache + undo stacks) —
      // the server keeps its rows and the next snapshot fetch restores them.
      const { workspaceId, assets } = get()
      const accountId = captureLibraryTransportOwner()?.accountId
      for (const a of assets) {
        if (a.src.startsWith('idb:')) {
          // Same key derivation as cacheBlobUrl/getAssetUrl — the bare id was
          // never a cache key, so object URLs leaked across resets.
          const key = a.blobKey ?? `blob:${a.id}`
          revokeCachedAssetUrls(key)
          // Both the current blob key and the original slot (replace-media
          // parks old pixels under the original key for undo).
          if (a.blobKey && a.blobKey !== `blob:${a.id}`) {
            void idbDelete(accountBlobStorageKey(a.blobKey, accountId))
          }
          void idbDelete(accountBlobStorageKey(`blob:${a.id}`, accountId))
          void idbDelete(accountBlobStorageKey(`poster:${key}`, accountId))
          void idbDelete(accountBlobStorageKey(`blob:poster:${a.id}`, accountId))
        }
      }
      undoStacks.delete(workspaceId)
      set({
        ...EMPTY_ROWS,
        hydrated: true,
        undoDepth: 0,
        redoDepth: 0,
      })
    },

    importCommunityLibrary: (seed, roots) => {
      const s = get()
      const rootSet = new Set(roots)
      const rootOf = (folder: string | null) => folder?.split('/')[0] ?? ''
      const existing = new Set(s.collections.filter(c => !c.deletedAt).map(c => c.id))
      const selected = seed.collections.filter(c => rootSet.has(rootOf(c.folder)) && !existing.has(c.id))
      if (!selected.length) return
      const colIds = new Set(selected.map(c => c.id))
      // Live-key check only — a soft-deleted key must ship property.create so
      // the executor/server revive it (both treat create-on-deleted-key as
      // revive; a live key is their no-op).
      const properties = seed.properties.filter(p => !s.properties.some(x => x.key === p.key && !x.deletedAt))
      // An attr survives only if it will resolve here: its definition is in
      // this import, or a live property with the same key AND type already
      // exists. Select is excluded — its values are option ids from the
      // source's vocabulary, which never match a colliding local one.
      // ponytail: on a live same-key collision those source values are
      // deliberately dropped, not remapped — merging option vocabularies into
      // the USER'S property would mutate their curation, and key aliasing
      // doesn't exist in the model. Revisit with linked collections (phase 2).
      const importedKeys = new Set(properties.map(p => p.key))
      const liveTypeByKey = new Map(s.properties.filter(x => !x.deletedAt).map(x => [x.key, x.type]))
      const seedTypeByKey = new Map(seed.properties.map(p => [p.key, p.type]))
      const attrResolves = (key: string) => {
        if (importedKeys.has(key)) return true
        const srcType = seedTypeByKey.get(key)
        return srcType !== undefined && srcType !== 'select' && liveTypeByKey.get(key) === srcType
      }
      const assets = seed.assets
        .filter(a => colIds.has(a.collectionId))
        .map(a => ({
          ...a,
          attrs: Object.fromEntries(Object.entries(a.attrs).filter(([key]) => attrResolves(key))),
        }))
      // Never write a dangling hero pointer (the source may hold assets this
      // import filtered out) — readers fall back to the first image anyway.
      const assetIds = new Set(assets.map(a => a.id))
      const collections = selected.map(c =>
        c.heroAssetId && !assetIds.has(c.heroAssetId) ? { ...c, heroAssetId: null } : c,
      )
      // folder.create appends blindly (line-of-sight in execute) — only mint
      // paths this workspace doesn't already have.
      const folders = seed.folders.filter(path => rootSet.has(path.split('/')[0]) && !s.folders.includes(path))
      const tags = [...new Set(collections.flatMap(c => c.tags))]
      const commands: LibraryCommand[] = [
        ...folders.map(path => ({ type: 'folder.create', path }) as LibraryCommand),
        ...properties.map(property => ({ type: 'property.create', property }) as LibraryCommand),
        ...collections.map(collection => ({ type: 'collection.create', collection }) as LibraryCommand),
        ...assets.map(asset => ({ type: 'asset.create', asset }) as LibraryCommand),
        ...(tags.length ? [{ type: 'tag.addToVocab', tags } as LibraryCommand] : []),
      ]
      // The server rejects requests over 200 flattened commands — a big
      // library in one batch would 400 and drop the whole optimistic import.
      // Slice into order-preserving sub-batches (FIFO queue keeps structure
      // rows ahead of the assets that reference them). Large imports undo in
      // a few steps instead of one — accepted trade.
      const IMPORT_CHUNK = 100
      for (let i = 0; i < commands.length; i += IMPORT_CHUNK) {
        s.dispatch(
          { type: 'batch', label: 'Import community library', commands: commands.slice(i, i + IMPORT_CHUNK) },
          { label: 'Import community library' },
        )
      }
      trackLibraryPackImported({
        workspaceId: s.workspaceId,
        packCount: roots.length,
        collectionCount: collections.length,
        assetCount: assets.length,
        propertyCount: properties.length,
      })
    },

    dispatch: (command, opts) => {
      const state = get()
      if (state.accessDenied || state.guestProjectScope || isLibraryWorkspaceAccessDenied(state.workspaceId)) return
      if (!state.hydrated || isGhostReadOnlyWorkspace(state.workspaceId)) return
      const result = execute(state, command)
      if (!result) return
      const [patch, inverse] = result
      persistPatch(patch)
      // Ship unless the caller owns transport for this command (upload flows
      // enqueue a materialize-when-ready entry instead). record:false system
      // writes still ship — they're real state changes.
      if (opts?.ship !== false) enqueueLibraryCommand(state.workspaceId, command)
      if (opts?.record === false) return
      const stacks = stacksFor(state.workspaceId)
      stacks.undo.push({ label: opts?.label ?? command.type, command, inverse })
      if (stacks.undo.length > UNDO_CAP) stacks.undo.shift()
      stacks.redo.length = 0
      set({ undoDepth: stacks.undo.length, redoDepth: 0 })
    },

    undo: () => {
      const state = get()
      if (state.accessDenied || state.guestProjectScope || isLibraryWorkspaceAccessDenied(state.workspaceId))
        return null
      if (!state.hydrated || isGhostReadOnlyWorkspace(state.workspaceId)) return null
      const stacks = stacksFor(state.workspaceId)
      const entry = stacks.undo.pop()
      if (!entry) return null
      const result = execute(state, entry.inverse)
      if (result) {
        persistPatch(result[0])
        enqueueLibraryCommand(state.workspaceId, entry.inverse)
      }
      stacks.redo.push(entry)
      set({ undoDepth: stacks.undo.length, redoDepth: stacks.redo.length })
      return entry.label
    },

    redo: () => {
      const state = get()
      if (state.accessDenied || state.guestProjectScope || isLibraryWorkspaceAccessDenied(state.workspaceId))
        return null
      if (!state.hydrated || isGhostReadOnlyWorkspace(state.workspaceId)) return null
      const stacks = stacksFor(state.workspaceId)
      const entry = stacks.redo.pop()
      if (!entry) return null
      const result = execute(state, entry.command)
      if (result) {
        persistPatch(result[0])
        enqueueLibraryCommand(state.workspaceId, entry.command)
      }
      stacks.undo.push(entry)
      set({ undoDepth: stacks.undo.length, redoDepth: stacks.redo.length })
      return entry.label
    },

    importFiles: async (files, collectionId) => {
      const state = get()
      if (state.accessDenied || state.guestProjectScope || isLibraryWorkspaceAccessDenied(state.workspaceId)) return ''
      if (!state.hydrated || isGhostReadOnlyWorkspace(state.workspaceId)) return collectionId ?? ''
      // The awaited blob writes/probes below can outlive a workspace switch —
      // commands built against one workspace must never dispatch into another.
      const startedIn = state.workspaceId
      const startedOwner = captureLibraryTransportOwner()
      if (!libraryAsyncOwnerIsCurrent(startedOwner)) return collectionId ?? ''
      const canFinishImport = () => {
        const current = get()
        return (
          current.workspaceId === startedIn &&
          !current.accessDenied &&
          !current.guestProjectScope &&
          !isLibraryWorkspaceAccessDenied(startedIn)
        )
      }
      // isImageFile catches empty-MIME Finder drags of allowed extensions; the
      // image/* prefix keeps formats the library accepts but the canvas
      // allowlist doesn't cover (e.g. GIF) importable.
      const media = files.filter(f => isImageFile(f) || f.type.startsWith('image/') || isSupportedLibraryVideoFile(f))
      if (media.length !== files.length) emitLibrarySaveFailedNotice()
      if (!media.length) return collectionId ?? ''
      const commands: LibraryCommand[] = []
      let target = collectionId
      if (!target) {
        const uploads = state.collections.find(c => c.name === 'Uploads' && !c.deletedAt)
        if (uploads) target = uploads.id
        else {
          const name = 'Uploads'
          target = uid()
          commands.push({
            type: 'collection.create',
            collection: {
              id: target,
              name,
              slug: toSlug(
                name,
                state.collections.map(c => c.slug),
              ),
              folder: null,
              tags: [],
              heroAssetId: null,
              usedIn: 0,
              rev: 1,
              createdAt: Date.now(),
              updatedAt: Date.now(),
            },
          })
        }
      }
      const now = Date.now()
      const uploads: LibraryUploadRequest[] = []
      const blobWrites: Array<{ key: string; file: File }> = []
      for (const f of media) {
        // HEIC/AVIF → JPEG/PNG before anything persists: IndexedDB poster,
        // probeMediaSize, and the R2 PUT all need a browser/provider-decodable
        // file. Passthrough for everything else.
        let file = f
        if (isSupportedLibraryVideoFile(f)) {
          file = normalizeLibraryVideoFile(f)
        } else {
          try {
            file = await normalizeImageFile(f)
            if (!libraryAsyncOwnerIsCurrent(startedOwner)) return collectionId ?? ''
          } catch (error) {
            console.warn('[library] import skipped: could not convert', f.name, error)
            captureSentryError(error, {
              operation: 'library_import_normalize_failed',
              component: 'libraryStore',
              level: 'warning',
              extra: { fileName: f.name, fileType: f.type, fileSize: f.size },
            })
            continue
          }
          if (!canFinishImport()) return ''
        }
        const id = uid()
        blobWrites.push({ key: `blob:${id}`, file })
        uploads.push({ assetId: id, file, mode: 'create' })
        commands.push({
          type: 'asset.create',
          asset: {
            id,
            collectionId: target,
            kind: isSupportedLibraryVideoFile(file) ? 'video' : 'image',
            src: `idb:${id}`,
            name: file.name.replace(/\.[a-z0-9]+$/i, ''),
            originalFilename: file.name,
            sizeBytes: file.size,
            sourceKind: 'upload',
            attrs: {},
            rev: 1,
            createdAt: now,
            updatedAt: now,
          },
        })
      }
      if (!uploads.length) return collectionId ?? ''
      if (!libraryAsyncOwnerIsCurrent(startedOwner)) return collectionId ?? ''
      if (!canFinishImport()) return ''
      try {
        // All selected source blobs commit in one IndexedDB transaction. Once
        // this await resumes there are no further async gaps before the full
        // optimistic batch and its resumable outbox entry are installed.
        await putAssetSourceBlobsAtomically(blobWrites, startedOwner?.accountId)
      } catch (error) {
        captureSentryError(error, {
          operation: 'library_import_blob_persist',
          component: 'libraryStore',
          extra: { fileCount: blobWrites.length },
        })
        emitLibrarySaveFailedNotice()
        return collectionId ?? ''
      }
      const current = get()
      if (!canFinishImport() || !current.hydrated || !libraryAsyncOwnerIsCurrent(startedOwner)) {
        // `personal` is shared by every login, so workspace, access, and owner
        // must all still match before selected bytes can enter an outbox.
        console.warn('[library] import dropped: workspace access changed while files were processing')
        await discardStagedLibraryBlobs(
          blobWrites.map(({ key }) => key),
          startedOwner?.accountId,
        )
        return ''
      }
      const label = `Upload ${uploads.length} file${uploads.length === 1 ? '' : 's'}`
      const batch: LibraryCommand = { type: 'batch', label, commands }
      // Local apply + undo now; the transport ships the batch once its R2
      // uploads finish (ship-time rewrite swaps idb: srcs for https).
      get().dispatch(batch, { label, ship: false })
      if (isLibraryTransportEnabled() && !enqueueLibraryCommandWithUploads(startedIn, batch, uploads, startedOwner)) {
        await discardStagedLibraryBlobs(
          blobWrites.map(({ key }) => key),
          startedOwner?.accountId,
        )
        return ''
      }
      // library_assets_added for uploads fires from onUploadComplete (below) —
      // counting here would count optimistic rows whose upload later fails.
      if (!collectionId) trackLibraryCollectionCreated({ workspaceId: startedIn, source: 'auto' })
      return target
    },
  }
})

// --- Selectors & vocabulary helpers ----------------------------------------------

/** Live (non-deleted) rows — what every surface renders. */
export const liveCollections = (s: Pick<LibraryState, 'collections'>) => s.collections.filter(c => !c.deletedAt)
export const liveAssets = (s: Pick<LibraryState, 'assets'>) => s.assets.filter(a => !a.deletedAt)

/** A collection's hero asset (explicit hero, else first live asset). */
export function heroAsset(
  assets: LibAsset[],
  collection: Pick<LibCollection, 'id' | 'heroAssetId'>,
): LibAsset | undefined {
  const own = assets.filter(a => a.collectionId === collection.id && !a.deletedAt)
  // Fallback prefers imagery — a text fragment can't be the face of a card.
  return own.find(a => a.id === collection.heroAssetId) ?? own.find(a => a.kind === 'image') ?? own[0]
}

/** Display label → optionId (for filters/inputs working in user words). */
export function optionIdFor(properties: Property[], key: string, value: string): string | undefined {
  return properties.find(p => p.key === key)?.options.find(o => o.value === value)?.id
}

// --- Public action façade ---------------------------------------------------------
// Thin helpers that build value-complete commands and dispatch them — the
// ergonomic API surfaces call. Each maps 1:1 onto a planned backend command
// (spec §5.1); the backend swap replaces dispatch's executor, never callers.

function S() {
  return useLibraryStore.getState()
}

const wsId = () => useLibraryStore.getState().workspaceId

export const libraryActions = {
  /**
   * Re-pull the current workspace snapshot. For rows created SERVER-SIDE while
   * this client's store was already hydrated (e.g. onboarding extraction) —
   * setWorkspace short-circuits once hydrated, so it can't pick them up.
   */
  refetch(): Promise<void> {
    return refetchCurrentSnapshot()
  },
  // ponytail: `type` is accepted-but-ignored — the collection Category concept is
  // gone. Callers still pass type:'' harmlessly; drop the param when they're touched.
  addCollection(input: { name: string; type?: string; folder?: string | null }): string {
    const s = S()
    if (
      !s.hydrated ||
      s.accessDenied ||
      s.guestProjectScope ||
      isLibraryWorkspaceAccessDenied(s.workspaceId) ||
      isGhostReadOnlyWorkspace(s.workspaceId)
    )
      return ''
    const id = uid()
    const now = Date.now()
    s.dispatch(
      {
        type: 'collection.create',
        collection: {
          id,
          name: input.name,
          slug: toSlug(
            input.name,
            s.collections.map(c => c.slug),
          ),
          folder: input.folder ?? null,
          tags: [],
          heroAssetId: null,
          usedIn: 0,
          rev: 1,
          createdAt: now,
          updatedAt: now,
        },
      },
      { label: 'New collection' },
    )
    trackLibraryCollectionCreated({ workspaceId: s.workspaceId, source: 'manual' })
    return id
  },
  /**
   * Capture a URL-backed asset (e.g. a canvas node) INTO the library: create a
   * LibAsset pointing at the ready https URL — no upload, no blob. Defaults to a
   * "Saved" collection when none is given. One undoable step; returns the id.
   */
  addAssetFromUrl(
    collectionId: string | null,
    media: {
      url: string
      kind: AssetKind
      name?: string
      width?: number
      height?: number
      posterUrl?: string
      origin?: LibAsset['origin']
      /** Provenance of the captured pixels; defaults to 'generation'. */
      sourceKind?: LibrarySourceKind
    },
  ): string {
    const s = S()
    if (
      !s.hydrated ||
      s.accessDenied ||
      s.guestProjectScope ||
      isLibraryWorkspaceAccessDenied(s.workspaceId) ||
      isGhostReadOnlyWorkspace(s.workspaceId)
    )
      return ''
    const now = Date.now()
    const commands: LibraryCommand[] = []
    let target = collectionId
    if (!target) {
      const existing = s.collections.find(c => c.name === 'Saved' && !c.deletedAt)
      if (existing) target = existing.id
      else {
        target = uid()
        commands.push({
          type: 'collection.create',
          collection: {
            id: target,
            name: 'Saved',
            slug: toSlug(
              'Saved',
              s.collections.map(c => c.slug),
            ),
            folder: null,
            tags: [],
            heroAssetId: null,
            usedIn: 0,
            rev: 1,
            createdAt: now,
            updatedAt: now,
          },
        })
      }
    }
    const assetId = uid()
    // Nameless fallback: "<Collection> <n>" — never a generic "Saved asset".
    const targetName = s.collections.find(c => c.id === target && !c.deletedAt)?.name ?? 'Saved'
    const nextNumber = s.assets.filter(a => a.collectionId === target && !a.deletedAt).length + 1
    commands.push({
      type: 'asset.create',
      asset: {
        id: assetId,
        collectionId: target,
        kind: media.kind,
        src: media.url,
        posterSrc: media.posterUrl,
        origin: media.origin,
        name: media.name?.trim() || `${targetName} ${nextNumber}`,
        width: media.width,
        height: media.height,
        sourceKind: media.sourceKind ?? 'generation',
        attrs: {},
        rev: 1,
        createdAt: now,
        updatedAt: now,
      },
    })
    s.dispatch({ type: 'batch', label: 'Save to library', commands }, { label: 'Save to library' })
    trackLibraryAssetsAdded({
      workspaceId: s.workspaceId,
      source: 'canvas',
      count: 1,
      imageCount: media.kind === 'image' ? 1 : 0,
      videoCount: media.kind === 'video' ? 1 : 0,
      textCount: media.kind === 'text' ? 1 : 0,
      assetSourceKind: media.sourceKind ?? 'generation',
    })
    if (commands.length > 1) trackLibraryCollectionCreated({ workspaceId: s.workspaceId, source: 'auto' })
    return assetId
  },
  renameCollection: (id: string, name: string) => {
    S().dispatch({ type: 'collection.rename', id, name }, { label: 'Rename collection' })
    trackLibraryCollectionEdited({ workspaceId: wsId(), action: 'rename' })
  },
  deleteCollection: (id: string) => {
    S().dispatch({ type: 'collection.delete', id }, { label: 'Delete collection' })
    trackLibraryCollectionEdited({ workspaceId: wsId(), action: 'delete' })
  },
  moveCollection: (id: string, folder: string | null) => {
    S().dispatch({ type: 'collection.move', id, folder }, { label: 'Move collection' })
    trackLibraryCollectionEdited({ workspaceId: wsId(), action: 'move' })
  },
  setCollectionTags: (id: string, tags: string[]) => {
    S().dispatch({ type: 'collection.setTags', id, tags }, { label: 'Edit tags' })
    trackLibraryCollectionEdited({ workspaceId: wsId(), action: 'set_tags', tagCount: tags.length })
  },
  setCollectionDescription: (id: string, description: string) => {
    S().dispatch(
      { type: 'collection.setDescription', id, description: description.trim() },
      { label: 'Edit description' },
    )
    trackLibraryCollectionEdited({ workspaceId: wsId(), action: 'set_description' })
  },
  setHero: (collectionId: string, assetId: string | null) => {
    S().dispatch({ type: 'collection.setHero', id: collectionId, assetId }, { label: 'Set hero' })
    trackLibraryCollectionEdited({ workspaceId: wsId(), action: 'set_hero' })
  },
  addFolder: (path: string) => {
    S().dispatch({ type: 'folder.create', path }, { label: 'New folder' })
    trackLibraryFolderEdited({ workspaceId: wsId(), action: 'create' })
  },
  renameFolder: (path: string, next: string) => {
    S().dispatch({ type: 'folder.rename', path, next }, { label: 'Rename folder' })
    trackLibraryFolderEdited({ workspaceId: wsId(), action: 'rename' })
  },
  deleteFolder: (path: string) => {
    S().dispatch({ type: 'folder.delete', path }, { label: 'Delete folder' })
    trackLibraryFolderEdited({ workspaceId: wsId(), action: 'delete' })
  },

  /** Bulk-add assets (sample/import paths). One undo entry. */
  addAssets(collectionId: string, list: Array<Pick<LibAsset, 'kind' | 'src' | 'srcLarge' | 'name' | 'attrs'>>) {
    const now = Date.now()
    S().dispatch(
      {
        type: 'batch',
        label: 'Add assets',
        commands: list.map(a => ({
          type: 'asset.create',
          asset: { ...a, id: uid(), collectionId, rev: 1, createdAt: now, updatedAt: now },
        })),
      },
      { label: `Add ${list.length} asset${list.length === 1 ? '' : 's'}` },
    )
    trackLibraryAssetsAdded({
      workspaceId: wsId(),
      source: 'bulk',
      count: list.length,
      imageCount: list.filter(a => a.kind === 'image').length,
      videoCount: list.filter(a => a.kind === 'video').length,
      textCount: list.filter(a => a.kind === 'text').length,
    })
  },
  renameAsset: (id: string, name: string) => {
    S().dispatch({ type: 'asset.rename', id, name }, { label: 'Rename asset' })
    trackLibraryAssetEdited({ workspaceId: wsId(), action: 'rename', count: 1 })
  },
  moveAssets: (ids: string[], collectionId: string) => {
    S().dispatch({ type: 'asset.move', ids, collectionId }, { label: 'Move assets' })
    trackLibraryAssetEdited({ workspaceId: wsId(), action: 'move', count: ids.length })
  },
  deleteAssets: (ids: string[]) => {
    S().dispatch({ type: 'asset.delete', ids }, { label: `Delete ${ids.length} asset${ids.length === 1 ? '' : 's'}` })
    trackLibraryAssetEdited({ workspaceId: wsId(), action: 'delete', count: ids.length })
  },

  /** Set a property value directly (null clears). Value shape follows the
      property type: optionId (select), string (text), number, or boolean
      (checkbox). */
  setAssetValue: (id: string, key: string, value: AttrValue | null) => {
    const s = S()
    s.dispatch({ type: 'asset.setValue', id, key, value }, { label: 'Edit property' })
    trackLibraryPropertyValueSet({
      workspaceId: s.workspaceId,
      propertyType: s.properties.find(p => p.key === key && !p.deletedAt)?.type,
      cleared: value === null,
    })
  },

  /** Set by display label — resolves the option, creating it when new
      (Notion's type-to-create). One undo entry for create+set. */
  setAssetValueByLabel(id: string, key: string, label: string | null) {
    if (label === null) return this.setAssetValue(id, key, null)
    const s = S()
    const existing = optionIdFor(s.properties, key, label)
    if (existing) return this.setAssetValue(id, key, existing)
    const option: PropertyOption = { id: uid(), value: label.trim() }
    s.dispatch(
      {
        type: 'batch',
        label: 'Edit property',
        commands: [
          { type: 'property.addOption', key, option },
          { type: 'asset.setValue', id, key, value: option.id },
        ],
      },
      { label: 'Edit property' },
    )
    trackLibraryPropertyValueSet({
      workspaceId: s.workspaceId,
      propertyType: s.properties.find(p => p.key === key && !p.deletedAt)?.type,
      cleared: false,
    })
  },

  /** Create a property of `type` (default text) from the user's label;
      returns its stable key. */
  addProperty(label: string, type: LibraryPropertyType = 'text', config?: LibraryPropertyConfig): string {
    const s = S()
    // Properties are workspace-wide: re-typing an existing property's name
    // binds to it — a second "Mood" key would fork the vocabulary.
    const existing = s.properties.find(p => !p.deletedAt && p.label.trim().toLowerCase() === label.trim().toLowerCase())
    if (existing) return existing.key
    const key = toPropertyKey(
      label,
      s.properties.map(p => p.key),
    )
    s.dispatch(
      {
        type: 'property.create',
        property: {
          id: uid(),
          key,
          label: label.trim(),
          type,
          options: [],
          ...(config ? { config } : {}),
          sortOrder: s.properties.length,
        },
      },
      { label: 'New property' },
    )
    trackLibraryPropertyCreated({ workspaceId: s.workspaceId, propertyType: type })
    return key
  },
  addPropertyOption(key: string, value: string): string {
    const option: PropertyOption = { id: uid(), value: value.trim() }
    S().dispatch({ type: 'property.addOption', key, option }, { label: 'New option' })
    trackLibraryPropertyEdited({ workspaceId: wsId(), action: 'add_option' })
    return option.id
  },
  /** Change a property's field type in place; values are kept (readers are
      shape-tolerant) and the option vocabulary stays dormant off-select. */
  setPropertyType: (key: string, propertyType: LibraryPropertyType, config?: LibraryPropertyConfig) => {
    S().dispatch({ type: 'property.setType', key, propertyType, config }, { label: 'Change property type' })
    trackLibraryPropertyEdited({ workspaceId: wsId(), action: 'set_type', propertyType })
  },
  /** Rename the user's word for a property; the key (attrs) never changes. */
  renameProperty: (key: string, label: string) => {
    S().dispatch({ type: 'property.rename', key, label: label.trim() }, { label: 'Rename property' })
    trackLibraryPropertyEdited({ workspaceId: wsId(), action: 'rename' })
  },
  /** Soft-delete a property everywhere; cells survive hidden, undo restores. */
  deleteProperty: (key: string) => {
    S().dispatch({ type: 'property.delete', key }, { label: 'Delete property' })
    trackLibraryPropertyEdited({ workspaceId: wsId(), action: 'delete' })
  },
  /** One gesture, one undo: create a property WITH its first value on an asset
      (property + cell in a single batch). Returns the stable key. */
  addPropertyWithValue(assetId: string, label: string, value: string): string {
    const s = S()
    // Same reuse rule as addProperty: an existing label binds to that property.
    const existing = s.properties.find(p => !p.deletedAt && p.label.trim().toLowerCase() === label.trim().toLowerCase())
    if (existing) {
      const v = value.trim()
      if (existing.type === 'select') {
        const optId = optionIdFor(s.properties, existing.key, v)
        if (optId) {
          s.dispatch(
            { type: 'asset.setValue', id: assetId, key: existing.key, value: optId },
            { label: 'Add property' },
          )
        } else {
          const option: PropertyOption = { id: uid(), value: v }
          s.dispatch(
            {
              type: 'batch',
              label: 'Add property',
              commands: [
                { type: 'property.addOption', key: existing.key, option },
                { type: 'asset.setValue', id: assetId, key: existing.key, value: option.id },
              ],
            },
            { label: 'Add property' },
          )
        }
      } else {
        const raw: AttrValue | null = existing.type === 'number' ? (Number.isFinite(Number(v)) ? Number(v) : null) : v
        if (raw !== null)
          s.dispatch({ type: 'asset.setValue', id: assetId, key: existing.key, value: raw }, { label: 'Add property' })
      }
      trackLibraryPropertyValueSet({ workspaceId: s.workspaceId, propertyType: existing.type, cleared: false })
      return existing.key
    }
    const key = toPropertyKey(
      label,
      s.properties.map(p => p.key),
    )
    s.dispatch(
      {
        type: 'batch',
        label: 'Add property',
        commands: [
          {
            type: 'property.create',
            property: {
              id: uid(),
              key,
              label: label.trim(),
              type: 'text',
              options: [],
              sortOrder: s.properties.length,
            },
          },
          { type: 'asset.setValue', id: assetId, key, value: value.trim() },
        ],
      },
      { label: 'Add property' },
    )
    trackLibraryPropertyCreated({ workspaceId: s.workspaceId, propertyType: 'text' })
    trackLibraryPropertyValueSet({ workspaceId: s.workspaceId, propertyType: 'text', cleared: false })
    return key
  },
  renamePropertyOption: (key: string, optionId: string, value: string) => {
    S().dispatch({ type: 'property.renameOption', key, optionId, value }, { label: 'Rename option' })
    trackLibraryPropertyEdited({ workspaceId: wsId(), action: 'rename_option' })
  },

  /** Replace an asset's pixels under the SAME id (the spec's version move:
      identity stable, pixels swap). Old blobs stay parked; undo repoints —
      including back to an https sample source. */
  async replaceAssetMedia(id: string, file: File): Promise<void> {
    const s = S()
    if (isGhostReadOnlyWorkspace(s.workspaceId)) return
    const startedIn = s.workspaceId
    const startedOwner = captureLibraryTransportOwner()
    if (!libraryAsyncOwnerIsCurrent(startedOwner)) return
    // Same normalization as importFiles — raw HEIC/AVIF must never reach the
    // poster blob, probeMediaSize, or the R2 PUT.
    if (isSupportedLibraryVideoFile(file)) {
      file = normalizeLibraryVideoFile(file)
    } else {
      if (!isImageFile(file) && !file.type.startsWith('image/')) {
        emitLibrarySaveFailedNotice()
        return
      }
      try {
        file = await normalizeImageFile(file)
        if (!libraryAsyncOwnerIsCurrent(startedOwner)) return
      } catch (error) {
        console.warn('[library] replace dropped: could not convert', file.name, error)
        captureSentryError(error, {
          operation: 'library_replace_normalize_failed',
          component: 'libraryStore',
          level: 'warning',
          extra: { fileName: file.name, fileType: file.type, fileSize: file.size },
        })
        return
      }
    }
    // Asset (and its rev, which seeds the blob key) is read AFTER the
    // conversion await, so a replacement that landed meanwhile bumps the rev
    // we derive from instead of both writes sharing one key.
    const asset = S().assets.find(a => a.id === id)
    if (!asset) return
    const key = `blob:${id}:r${asset.rev + 1}`
    let poster: Blob | null
    try {
      poster = await putAssetBlobAt(key, file, startedOwner?.accountId)
    } catch (error) {
      await discardStagedLibraryBlobs([key], startedOwner?.accountId)
      captureSentryError(error, {
        operation: 'library_replace_blob_persist',
        component: 'libraryStore',
        extra: { assetId: id },
      })
      emitLibrarySaveFailedNotice()
      return
    }
    if (!libraryAsyncOwnerIsCurrent(startedOwner)) {
      await discardStagedLibraryBlobs([key], startedOwner?.accountId)
      return
    }
    const dims = await probeMediaSize(file)
    if (!libraryAsyncOwnerIsCurrent(startedOwner)) {
      await discardStagedLibraryBlobs([key], startedOwner?.accountId)
      return
    }
    // Same guard as importFiles: never dispatch into a workspace the command
    // wasn't built against.
    if (S().workspaceId !== startedIn || !S().hydrated) {
      console.warn('[library] replace dropped: workspace or account changed while media was processing')
      await discardStagedLibraryBlobs([key], startedOwner?.accountId)
      return
    }
    const command: LibraryCommand = {
      type: 'asset.update',
      id,
      patch: {
        src: `idb:${id}`,
        blobKey: key,
        kind: isSupportedLibraryVideoFile(file) ? 'video' : 'image',
        originalFilename: file.name,
        sizeBytes: file.size,
        width: dims?.width ?? null,
        height: dims?.height ?? null,
        srcLarge: null,
        posterSrc: null,
        r2Key: null,
        posterR2Key: null,
        sourceKind: 'upload',
      },
      // Appends to history: undo moves the pointer back, never re-appends.
      versionId: uid(),
    }
    // Local apply + undo now; the transport ships once the new pixels are on
    // R2 (rewrite keyed by blobKey — undo/redo across replaces stays correct).
    s.dispatch(command, { label: 'Replace media', ship: false })
    if (
      isLibraryTransportEnabled() &&
      !enqueueLibraryCommandWithUploads(
        startedIn,
        command,
        [
          {
            assetId: id,
            file,
            blobKey: key,
            poster,
            width: dims?.width,
            height: dims?.height,
            mode: 'replace',
          },
        ],
        startedOwner,
      )
    ) {
      await discardStagedLibraryBlobs([key], startedOwner?.accountId)
    }
    // replace_media is tracked from onUploadComplete — a failed upload keeps
    // the old media and must not count as a replacement.
  },

  /** History restore: point the asset back at an older version (pointer move). */
  setAssetCurrentVersion: (id: string, versionId: string) => {
    S().dispatch({ type: 'asset.setCurrentVersion', id, versionId }, { label: 'Restore version' })
    trackLibraryAssetEdited({ workspaceId: wsId(), action: 'restore_version', count: 1 })
  },

  /** Enable one more collection for a project (the @-menu inline confirm). */
  enableCollectionForProject(projectId: string, collectionId: string) {
    const s = S()
    const existing = s.projectLinks[projectId] ?? []
    if (existing.includes(collectionId)) return
    s.dispatch(
      { type: 'link.setProject', projectId, collectionIds: [...existing, collectionId] },
      { label: 'Add to project' },
    )
    trackLibraryProjectLinkChanged({
      workspaceId: s.workspaceId,
      projectId,
      action: 'enable',
      enabledCount: existing.length + 1,
    })
  },

  /** Save a prompt-text fragment INTO a collection (kind='text' asset — a
      variant of the subject, like any still). null collection → 'Saved'. */
  addTextFragment(collectionId: string | null, input: { name: string; text?: string }): string {
    const s = S()
    if (
      !s.hydrated ||
      s.accessDenied ||
      s.guestProjectScope ||
      isLibraryWorkspaceAccessDenied(s.workspaceId) ||
      isGhostReadOnlyWorkspace(s.workspaceId)
    )
      return ''
    const now = Date.now()
    const commands: LibraryCommand[] = []
    let target = collectionId
    if (!target) {
      const existing = s.collections.find(c => c.name === 'Saved' && !c.deletedAt)
      if (existing) target = existing.id
      else {
        target = uid()
        commands.push({
          type: 'collection.create',
          collection: {
            id: target,
            name: 'Saved',
            slug: toSlug(
              'Saved',
              s.collections.map(c => c.slug),
            ),
            folder: null,
            tags: [],
            heroAssetId: null,
            usedIn: 0,
            rev: 1,
            createdAt: now,
            updatedAt: now,
          },
        })
      }
    }
    const assetId = uid()
    commands.push({
      type: 'asset.create',
      asset: {
        id: assetId,
        collectionId: target,
        kind: 'text',
        src: '',
        textValue: input.text ?? '',
        name: input.name.trim() || 'Fragment',
        attrs: {},
        rev: 1,
        createdAt: now,
        updatedAt: now,
      },
    })
    s.dispatch({ type: 'batch', label: 'Save text to library', commands }, { label: 'Save text to library' })
    trackLibraryAssetsAdded({
      workspaceId: s.workspaceId,
      source: 'text_fragment',
      count: 1,
      imageCount: 0,
      videoCount: 0,
      textCount: 1,
    })
    if (commands.length > 1) trackLibraryCollectionCreated({ workspaceId: s.workspaceId, source: 'auto' })
    return assetId
  },
  /** Edit a text fragment's body (rename via renameAsset). */
  updateTextFragment: (id: string, text: string) => {
    S().dispatch({ type: 'asset.update', id, patch: { textValue: text } }, { label: 'Edit text' })
    trackLibraryAssetEdited({ workspaceId: wsId(), action: 'edit_text', count: 1 })
  },

  saveView(name: string, definition: ViewDefinition): string {
    const id = uid()
    const now = Date.now()
    S().dispatch(
      { type: 'view.save', view: { id, name, definition, rev: 1, createdAt: now, updatedAt: now } },
      { label: 'Save view' },
    )
    trackLibrarySavedViewEdited({ workspaceId: wsId(), action: 'save' })
    return id
  },
  updateView: (id: string, patch: { name?: string; definition?: ViewDefinition }) => {
    S().dispatch({ type: 'view.update', id, ...patch }, { label: 'Update view' })
    trackLibrarySavedViewEdited({ workspaceId: wsId(), action: 'update' })
  },
  deleteView: (id: string) => {
    S().dispatch({ type: 'view.delete', id }, { label: 'Delete view' })
    trackLibrarySavedViewEdited({ workspaceId: wsId(), action: 'delete' })
  },
}

// --- Transport bridge ---------------------------------------------------------------
// The transport can't import this module (we import it), so it calls back
// through this bridge: local https swap after an upload, cleanup after a
// failed one, and snapshot refetches (queue dropped on 4xx / window focus).

/** Refetch the current workspace's snapshot and replace local rows (LWW). */
async function refetchCurrentSnapshot(): Promise<void> {
  const ws = useLibraryStore.getState().workspaceId
  const token = ++workspaceLoadToken
  // A guest-scoped view refreshes through the project-scoped read — the vault
  // fetch would SUCCEED for a personal-project guest (it's their own vault)
  // and stomp the scoped rows while the scope label stayed on. This is also
  // how a failed-closed empty scope recovers on window focus/online.
  const scope = useLibraryStore.getState().guestProjectScope
  if (scope) {
    const scoped = await fetchProjectScopedLibrarySnapshot(ws, scope)
    if (!scoped || token !== workspaceLoadToken) return
    const current = useLibraryStore.getState()
    if (current.workspaceId !== ws || current.guestProjectScope !== scope) return
    const snap = recoverLegacyLibrarySnapshotMedia(scoped)
    // No pending replay: guest writes can never ship.
    useLibraryStore.setState({
      collections: snap.collections,
      assets: snap.assets,
      versions: snap.versions ?? [],
      folders: snap.folders,
      tagVocab: snap.tagVocab,
      properties: snap.properties,
      views: snap.views,
      projectLinks: snap.projectLinks,
      members: snap.members ?? {},
    })
    return
  }
  // Captured BEFORE the fetch: a command acked while the fetch is in flight
  // clears its pending marker, but the snapshot predates the ack — without
  // this capture the merge would drop the freshly-accepted row.
  const pendingBefore = getPendingLibraryCommands(ws)
  const rawSnapshot = await fetchLibrarySnapshot(ws)
  const snap = rawSnapshot ? recoverLegacyLibrarySnapshotMedia(rawSnapshot) : null
  if (!snap || token !== workspaceLoadToken) return
  if (useLibraryStore.getState().workspaceId !== ws) return
  const pendingForReplay = () => unionPendingLibraryCommands(pendingBefore, getPendingLibraryCommands(ws))
  await Promise.all(
    replayPendingLibraryCommands(snap, pendingForReplay())
      .assets.filter(a => a.src.startsWith('idb:'))
      .map(a => cacheBlobUrl(a)),
  )
  if (token !== workspaceLoadToken) return
  // Re-merge against CURRENT state: an optimistic dispatch can land during the
  // blob-URL warmup await above, and a merge computed before it would stomp it.
  const reconciled = replayPendingLibraryCommands(snap, pendingForReplay())
  useLibraryStore.setState({
    collections: reconciled.collections,
    assets: reconciled.assets,
    versions: reconciled.versions ?? [],
    folders: reconciled.folders,
    tagVocab: reconciled.tagVocab,
    properties: reconciled.properties,
    views: reconciled.views,
    projectLinks: reconciled.projectLinks,
    members: reconciled.members ?? {},
    hydrated: true,
  })
}

registerLibraryStoreBridge({
  onAccountChanged: () => {
    // `personal` is an auth-relative alias, so workspaceId alone cannot prove
    // these rows/stacks belong to the next session. Cancel in-flight snapshots
    // and clear every account-local in-memory surface before it can render or
    // dispatch under the new user's transport binding. Preserve the intended
    // workspace: a team project can mount before async auth initialization,
    // and resetting it to `personal` would silently change Library scope.
    const workspaceId = useLibraryStore.getState().workspaceId
    workspaceLoadToken++
    undoStacks.clear()
    snapshotTracked.clear()
    clearCachedAssetUrls()
    useLibraryStore.setState({
      ...EMPTY_ROWS,
      workspaceId,
      hydrated: false,
      // The incoming identity's access is unknown until it is proved: a guest
      // who just signed up must not inherit their own requires-account denial.
      accessDenied: false,
      accessDeniedReason: null,
      loadFailed: false,
      undoDepth: 0,
      redoDepth: 0,
    })
  },
  onAccountReady: () => {
    // Account binding happens after the project panel may already have tried
    // to hydrate. Retry only once the correctly-owned durable queue is loaded,
    // so the snapshot and optimistic replay belong to the same user.
    const state = useLibraryStore.getState()
    if (!state.hydrated) void state.setWorkspace(state.workspaceId, { replayLastContext: true })
  },
  loadUploadRequest: async (upload: LibraryDurableUpload) => {
    const owner = captureLibraryTransportOwner()
    if (!owner) return null
    const key = upload.blobKey ?? `blob:${upload.assetId}`
    const stored = await idbGet<Blob>(accountBlobStorageKey(key, owner.accountId))
    // A stale/colliding key must never upload different bytes under a durable
    // descriptor, especially after an account transition on a shared browser.
    if (!stored || stored.size !== upload.sizeBytes) return null
    const file = new File([stored], upload.fileName, {
      type: upload.contentType || stored.type,
      lastModified: upload.lastModified,
    })
    let poster: Blob | null | undefined = await idbGet<Blob>(accountBlobStorageKey(`poster:${key}`, owner.accountId))
    if (!poster) {
      poster = await idbGet<Blob>(accountBlobStorageKey(`blob:poster:${upload.assetId}`, owner.accountId))
    }
    if (!poster && isSupportedLibraryVideoFile(file)) {
      poster = await capturePoster(file)
      if (poster) await idbSet(accountBlobStorageKey(`poster:${key}`, owner.accountId), poster)
    }
    const dimensions = await probeMediaSize(file)
    return {
      assetId: upload.assetId,
      file,
      blobKey: upload.blobKey,
      poster: poster ?? undefined,
      width: dimensions?.width,
      height: dimensions?.height,
      mode: upload.mode,
    }
  },
  cleanupUploadBlobs: async uploads => {
    const owner = captureLibraryTransportOwner()
    if (!owner) return
    const keys = uploads.flatMap(upload => {
      const key = upload.blobKey ?? `blob:${upload.assetId}`
      return [key, `poster:${key}`, ...(upload.blobKey ? [] : [`blob:poster:${upload.assetId}`])]
    })
    await idbDeleteMany(keys.map(key => accountBlobStorageKey(key, owner.accountId)))
    for (const upload of uploads) revokeCachedAssetUrls(upload.blobKey ?? `blob:${upload.assetId}`)
  },
  onUploadComplete: (workspaceId, assetId, patch, expectedBlobKey, mode) => {
    const s = useLibraryStore.getState()
    // If the user switched workspaces, the row isn't loaded — the https URLs
    // are already persisted server-side and arrive with the next snapshot.
    if (s.workspaceId !== workspaceId || !s.hydrated) return
    const asset = s.assets.find(a => a.id === assetId)
    if (!shouldApplyUploadCompletion(asset, expectedBlobKey)) return
    // Adds/replacements count HERE, not at dispatch — an optimistic row whose
    // upload fails is rolled back and must never reach the dashboard.
    if (mode === 'create') {
      trackLibraryAssetsAdded({
        workspaceId,
        source: 'upload',
        count: 1,
        imageCount: asset?.kind === 'image' ? 1 : 0,
        videoCount: asset?.kind === 'video' ? 1 : 0,
        textCount: 0,
      })
    } else {
      trackLibraryAssetEdited({ workspaceId, action: 'replace_media', count: 1 })
    }
    // Invisible to undo (record:false) and already shipping inside the
    // rewritten create/update (ship:false) — this is purely the local swap
    // that lets @mentions offer the asset and other surfaces use https.
    s.dispatch({ type: 'asset.update', id: assetId, patch }, { record: false, ship: false })
    // The optimistic version rows (create's v1, replace appends) baked the
    // idb: src too — swap them as well, or restoring that version before the
    // next snapshot copies a browser-local URL back onto the asset. Server
    // rows never saw idb: (the ship-time rewrite fixes the command), so this
    // is local reconciliation only. Approximate on purpose: version rows
    // don't carry blobKey, so two in-flight replaces of one asset can stamp
    // the later URL on both rows until the next snapshot corrects them.
    if (patch.src) {
      useLibraryStore.setState(state => ({
        versions: state.versions.map(v =>
          v.assetId === assetId && v.src.startsWith('idb:')
            ? {
                ...v,
                src: patch.src as string,
                ...(patch.posterSrc !== undefined ? { posterSrc: patch.posterSrc } : {}),
                ...(patch.r2Key !== undefined ? { r2Key: patch.r2Key } : {}),
                ...(patch.posterR2Key !== undefined ? { posterR2Key: patch.posterR2Key } : {}),
              }
            : v,
        ),
      }))
    }
  },
  onUploadFailed: (workspaceId, assetId, mode) => {
    const s = useLibraryStore.getState()
    if (s.workspaceId !== workspaceId || !s.hydrated) return
    if (mode === 'create') {
      // The server never learned about this asset (its create gets dropped at
      // ship time) — remove the optimistic local row too.
      s.dispatch({ type: 'asset.delete', ids: [assetId] }, { record: false, ship: false })
    } else {
      // The optimistic replacement points at the staged idb: blob. That blob is
      // cleaned once the terminal failure leaves the queue, so restore the
      // server's previous media instead of leaving a broken local preview.
      void refetchCurrentSnapshot()
    }
    emitLibrarySaveFailedNotice()
  },
  onQueueDropped: workspaceId => {
    // The server refused a batch (4xx): local optimistic state is stale.
    if (useLibraryStore.getState().workspaceId !== workspaceId) return
    void refetchCurrentSnapshot()
    emitLibrarySaveFailedNotice()
  },
  onAccessDenied: (workspaceId, reason) => {
    const s = useLibraryStore.getState()
    if (s.workspaceId !== workspaceId) return
    undoStacks.delete(workspaceId)
    useLibraryStore.setState({
      ...EMPTY_ROWS,
      accessDenied: true,
      accessDeniedReason: reason,
      hydrated: true,
      loadFailed: false,
      undoDepth: 0,
      redoDepth: 0,
    })
    // A guest never had access to lose: this is the library's opening state for
    // them, not a save that failed. Toasting it would fire on every mount.
    if (reason !== 'requires-account') emitLibrarySaveFailedNotice('access-denied')
  },
  onAccessRestored: workspaceId => {
    if (useLibraryStore.getState().workspaceId !== workspaceId) return
    useLibraryStore.setState({ accessDenied: false, accessDeniedReason: null })
  },
  refetchIfIdle: () => {
    const s = useLibraryStore.getState()
    if (!s.hydrated) return
    if (isLibraryQueueBusy(s.workspaceId)) return
    void refetchCurrentSnapshot()
  },
})
